
import { supabase } from '../lib/supabase';
import { auditLogger, AuditActionType, AuditLog } from '../lib/auditLogger';
import { Owner, Property, Deal, Agency, UserProfile, UserDeletionDependencies, SequenceTemplate, DocumentCategory, ChecklistTemplate, Proposal, DealStage, Contract, PipelineType, PropertyDocument, AIUsage, AIUsageType, AIUsageStats, MarketingProvider, MarketingProviderUser, MarketingLead, MarketingLeadFilters, MarketingLeadStats, MarketingLeadStatus, MarketingLeadStatusHistory, MarketingLeadSource, LeadSource, Expense, Revenue, PropertyROI, MonthlyFinancialSummary, ExpenseFilters, ExpenseType, ExpenseStatus, Reminder, MarketingLeadNote, MarketingLeadDocument, BillingConfig } from '../types';
import { addSubscriptionItem, removeSubscriptionItem } from './stripeService';
import { googleCalendarService } from './googleCalendarService';
import { parseCSV, mapCSVRowToLead, ImportLeadRow } from '../lib/csvParser';
import { parseFullName, parseAddress, normalizePhone, normalizeEmail, parseFrenchDate, extractLeboncoinUrl } from '../lib/importUtils';

// URL de l'application (récupérée depuis les variables d'environnement)
// @ts-ignore - Vite injecte VITE_APP_URL dans import.meta.env
const APP_URL = import.meta.env.VITE_APP_URL || 'https://manager.agences-placid.com';

const normalizeReminderForSync = (reminder: Reminder): Reminder => {
  const nowIso = new Date().toISOString();
  // Avec une heure renseignée => RDV (event Google Agenda).
  // Sans heure => tâche (Google Tasks) : on ne fixe pas de startAt/endAt artificiels.
  const hasTime = !!reminder.time && /^\d{2}:\d{2}/.test(reminder.time);
  if (hasTime) {
    const startSource = reminder.startAt || reminder.date;
    const startIso = new Date(startSource).toISOString();
    const endIso = reminder.endAt || new Date(new Date(startIso).getTime() + 60 * 60 * 1000).toISOString();
    return {
      ...reminder,
      startAt: startIso,
      endAt: endIso,
      timezone: reminder.timezone || 'Europe/Paris',
      title: reminder.title || reminder.note || 'Rendez-vous CRM',
      description: reminder.description || reminder.note || '',
      updatedAt: reminder.updatedAt || nowIso,
    };
  }
  return {
    ...reminder,
    startAt: undefined,
    endAt: undefined,
    timezone: reminder.timezone || 'Europe/Paris',
    title: reminder.title || reminder.note || 'Tâche CRM',
    description: reminder.description || reminder.note || '',
    updatedAt: reminder.updatedAt || nowIso,
  };
};

export const storage = {
  // --- Auth & Profiles ---
  getUser: async () => {
    const { data } = await supabase.auth.getSession();
    return data.session?.user || null;
  },

  getProfile: async (userId: string): Promise<UserProfile | null> => {
    // DB uses snake_case, App uses camelCase
    const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).single();
    
    if (error && error.code === '42P17') {
        console.error("CRITICAL: Infinite recursion detected via SQL. Please execute the fix script.");
        throw error;
    }

    if (error && error.code === 'PGRST116') {
        return null;
    }

    if (error) { console.error("Error fetching profile:", error); return null; }

    if (data) {
        // Si c'est un prestataire marketing, charger isSupervisor
        let isSupervisor = false;
        let marketingProviderId: string | null = null;
        let marketingAgencyIds: string[] = [];
        
        if (data.role === 'prestataire_marketing') {
            const { data: providerUser } = await supabase
                .from('marketing_provider_users')
                .select('id, is_supervisor, provider_id')
                .eq('user_id', userId)
                .maybeSingle(); // Utiliser maybeSingle() pour gérer le cas où l'utilisateur n'est pas encore lié
            
            if (providerUser) {
                isSupervisor = providerUser.is_supervisor || false;
                marketingProviderId = providerUser.provider_id || null;
                
                // Charger les agences assignées si setter (la table utilise user_id directement)
                if (!isSupervisor) {
                    const { data: userAgencies } = await supabase
                        .from('marketing_provider_user_agencies')
                        .select('agency_id')
                        .eq('user_id', userId);
                    
                    marketingAgencyIds = userAgencies?.map((a: any) => a.agency_id) || [];
                }
            }
        }
        
        return {
            id: data.id,
            email: data.email,
            role: data.role,
            accessLevel: data.access_level, // Mapped from access_level
            firstName: data.first_name,     // Mapped from first_name
            lastName: data.last_name,        // Mapped from last_name
            isSupervisor: isSupervisor,
            marketingProviderId: marketingProviderId,
            marketingAgencyIds: marketingAgencyIds
        };
    }
    return null;
  },

  logout: async () => {
    await supabase.auth.signOut();
  },

  sendPasswordReset: async (email: string) => {
      // Redirige vers l'URL de production après le clic sur le lien email
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${APP_URL}`
      });
      return { error };
  },

  updateUserPassword: async (password: string) => {
      const { data, error } = await supabase.auth.updateUser({
          password: password,
          data: { must_change_password: false } // Reset flag
      });
      return { data, error };
  },

  // --- Core Data ---

  createUser: async (userData: any) => {
      const tempPassword = Math.random().toString(36).slice(-8) + "Placid!1" + Math.random().toString(36).slice(-4);

      const { data, error } = await supabase.auth.signUp({
          email: userData.email,
          password: tempPassword,
          options: {
              data: {
                  first_name: userData.firstName,
                  last_name: userData.lastName,
                  role: userData.role,
                  access_level: userData.accessLevel,
                  must_change_password: true // Flag pour forcer le changement au premier login
              },
              // Force la redirection vers l'URL de prod pour éviter le localhost
              emailRedirectTo: `${APP_URL}`
          }
      });
      
      if (error) return { error };

      // Facturation Stripe automatique (sauf pour superadmin et prestataires marketing)
      if (data.user && userData.role !== 'superadmin' && userData.role !== 'prestataire_marketing' && userData.accessLevel) {
          try {
              const stripeResult = await addSubscriptionItem(
                  userData.accessLevel,
                  userData.email,
                  `${userData.firstName} ${userData.lastName}`
              );
              if (!stripeResult.success) {
                  // Si la facturation échoue, on bloque la création selon les préférences utilisateur
                  // Pour l'instant, on log l'erreur mais on continue (mode dégradé)
                  console.error('Erreur lors de l\'ajout à l\'abonnement Stripe:', stripeResult.error);
                  // Optionnel: supprimer l'utilisateur créé si on veut bloquer
                  // await supabase.auth.admin.deleteUser(data.user.id);
                  // return { error: { message: `Erreur de facturation Stripe: ${stripeResult.error}` } };
              }
          } catch (stripeError: any) {
              console.error('Erreur lors de la facturation Stripe:', stripeError);
              // Mode dégradé: on continue même si Stripe échoue
          }
      }

      if (data.user && userData.agencyIds && userData.agencyIds.length > 0) {
          setTimeout(async () => {
              await storage.syncUserAgencies(data.user!.id, userData.agencyIds);
          }, 1000);
      }
      
      return { data: data.user, error: null };
  },

  getAllAgencies: async (): Promise<Agency[]> => {
      const { data, error } = await supabase.from('agencies').select('*');
      if (error) console.error(error);
      return data?.map((d: any) => ({
          id: d.id,
          name: d.name,
          city: d.city,
          address: d.address,
          legalStructure: d.legal_structure,
          capital: d.capital,
          siret: d.siret,
          rcsCity: d.rcs_city,
          email: d.email,
          phone: d.phone,
          yousignApiKey: d.yousign_api_key,
          yousignEnvironment: d.yousign_environment || 'sandbox',
          placidCommissionRate: d.placid_commission_rate,
          
      })) || [];
  },

  getUserAgencies: async (userId: string): Promise<string[]> => {
      const { data, error } = await supabase.from('user_agencies').select('agency_id').eq('user_id', userId);
      if (error) console.error(error);
      return data?.map((d: any) => d.agency_id) || [];
  },

  saveAgency: async (agency: Partial<Agency>) => {
      const dbAgency: any = { 
          id: agency.id,
          name: agency.name,
          city: agency.city,
          address: agency.address,
          legal_structure: agency.legalStructure,
          capital: agency.capital,
          siret: agency.siret,
          rcs_city: agency.rcsCity,
          email: agency.email,
          phone: agency.phone,
          yousign_api_key: agency.yousignApiKey,
          yousign_environment: agency.yousignEnvironment,
          placid_commission_rate: agency.placidCommissionRate
      };

      // Récupérer l'ancienne agence si c'est une mise à jour
      let oldAgency: any = null;
      if (dbAgency.id) {
        const { data: existingAgency } = await supabase.from('agencies').select('*').eq('id', dbAgency.id).single();
        if (existingAgency) oldAgency = existingAgency;
      }

      Object.keys(dbAgency).forEach(key => dbAgency[key] === undefined && delete dbAgency[key]);
      if (!dbAgency.id) delete dbAgency.id;

      const { data, error } = await supabase.from('agencies').upsert(dbAgency).select().single();
      
      // Logger l'audit
      if (data && !error) {
        const actionType = oldAgency ? 'UPDATE' : 'INSERT';
        await auditLogger.log(actionType, 'agencies', data.id, oldAgency, dbAgency);
      }
      
      return { data, error };
  },

  getAllProfiles: async (): Promise<UserProfile[]> => {
      const { data, error } = await supabase.from('profiles').select('*');
      if (error) {
          if (error.code === '42P17') throw error;
          console.error(error);
      }
      
      if (!data) return [];
      
      // Pour les prestataires marketing, charger les données supplémentaires
      const profilesWithMarketingData = await Promise.all(
          data.map(async (d: any) => {
              const profile: UserProfile = {
                  id: d.id,
                  email: d.email,
                  role: d.role,
                  accessLevel: d.access_level,
                  firstName: d.first_name,
                  lastName: d.last_name
              };
              
              // Si c'est un prestataire marketing, charger les données
              if (d.role === 'prestataire_marketing') {
                  const { data: providerUser } = await supabase
                      .from('marketing_provider_users')
                      .select('id, is_supervisor, provider_id')
                      .eq('user_id', d.id)
                      .maybeSingle();
                  
                  if (providerUser) {
                      profile.isSupervisor = providerUser.is_supervisor || false;
                      profile.marketingProviderId = providerUser.provider_id || null;
                      
                      // Charger les agences assignées si setter
                      if (!profile.isSupervisor) {
                          const { data: userAgencies } = await supabase
                              .from('marketing_provider_user_agencies')
                              .select('agency_id')
                              .eq('user_id', d.id);
                          
                          profile.marketingAgencyIds = userAgencies?.map((a: any) => a.agency_id) || [];
                      } else {
                          profile.marketingAgencyIds = [];
                      }
                  } else {
                      // Pas encore lié à un provider
                      profile.isSupervisor = false;
                      profile.marketingProviderId = null;
                      profile.marketingAgencyIds = [];
                  }
              }
              
              return profile;
          })
      );
      
      return profilesWithMarketingData;
  },

  updateProfile: async (id: string, updates: Partial<UserProfile>) => {
      // Récupérer l'ancien profil pour l'audit et la facturation Stripe
      const { data: oldProfile } = await supabase.from('profiles').select('*').eq('id', id).single();
      
      if (!oldProfile) {
          return { data: null, error: { message: 'Profil non trouvé' } };
      }

      const oldAccessLevel = oldProfile.access_level as 'national' | 'local' | null;
      const newAccessLevel = updates.accessLevel;
      const oldRole = oldProfile.role;
      const newRole = updates.role;
      
      const dbUpdates: any = {};
      if (updates.firstName !== undefined) dbUpdates.first_name = updates.firstName;
      if (updates.lastName !== undefined) dbUpdates.last_name = updates.lastName;
      if (updates.role !== undefined) dbUpdates.role = updates.role;
      if (updates.accessLevel !== undefined) dbUpdates.access_level = updates.accessLevel;

      const result = await supabase.from('profiles').update(dbUpdates).eq('id', id).select();
      
      // Logger l'audit
      if (result.data && result.data.length > 0 && !result.error && oldProfile) {
        await auditLogger.logUpdate('profiles', id, oldProfile, { ...oldProfile, ...dbUpdates });
      }

      // Mettre à jour l'abonnement Stripe si l'accessLevel a changé (sauf pour superadmin)
      if (result.data && result.data.length > 0 && !result.error) {
          const updatedProfile = result.data[0];
          const finalRole = newRole || oldRole;
          
          // Ne pas facturer les superadmins
          if (finalRole !== 'superadmin' && oldAccessLevel !== newAccessLevel) {
              try {
                  // Si l'ancien accessLevel existait, retirer l'item correspondant
                  if (oldAccessLevel === 'national' || oldAccessLevel === 'local') {
                      const removeResult = await removeSubscriptionItem(
                          oldAccessLevel,
                          oldProfile.email,
                          `${oldProfile.first_name} ${oldProfile.last_name}`
                      );
                      if (!removeResult.success) {
                          console.error('Erreur lors de la réduction de l\'abonnement Stripe:', removeResult.error);
                      }
                  }
                  
                  // Si le nouveau accessLevel existe, ajouter l'item correspondant
                  if (newAccessLevel === 'national' || newAccessLevel === 'local') {
                      const addResult = await addSubscriptionItem(
                          newAccessLevel,
                          updatedProfile.email || oldProfile.email,
                          `${updatedProfile.first_name || oldProfile.first_name} ${updatedProfile.last_name || oldProfile.last_name}`
                      );
                      if (!addResult.success) {
                          console.error('Erreur lors de l\'ajout à l\'abonnement Stripe:', addResult.error);
                      }
                  }
              } catch (stripeError: any) {
                  console.error('Erreur lors de la mise à jour de l\'abonnement Stripe:', stripeError);
                  // Mode dégradé: on continue même si Stripe échoue
              }
          }
      }
      
      return result;
  },

  getUserDeletionDependencies: async (userId: string) => {
      const { data, error } = await supabase.rpc('get_user_deletion_dependencies', {
          p_user_id: userId,
      });
      if (error) {
          console.error('getUserDeletionDependencies error:', error);
          return { data: null, error };
      }
      return { data: data as UserDeletionDependencies, error: null };
  },

  getEligibleReassignmentTargets: async (fromUserId: string): Promise<UserProfile[]> => {
      const { data, error } = await supabase.rpc('get_eligible_reassignment_targets', {
          p_from_user_id: fromUserId,
      });

      if (error) {
          console.error('getEligibleReassignmentTargets error:', error);
          return [];
      }

      if (!Array.isArray(data)) return [];

      return (data as Array<{
          id: string;
          email: string;
          firstName?: string | null;
          lastName?: string | null;
          role: UserProfile['role'];
          accessLevel?: UserProfile['accessLevel'];
      }>).map((row) => ({
          id: row.id,
          email: row.email,
          role: row.role,
          accessLevel: row.accessLevel ?? 'local',
          firstName: row.firstName ?? undefined,
          lastName: row.lastName ?? undefined,
      }));
  },

  deleteUser: async (
      userId: string,
      reassignToUserId?: string | null
  ): Promise<{ error?: any; dependencies?: UserDeletionDependencies }> => {
      // Récupérer le profil avant suppression pour vérifier et la facturation
      const profile = await storage.getProfile(userId);
      if (!profile) {
          return { error: { message: 'Utilisateur non trouvé' } };
      }

      // Ne pas permettre la suppression d'un superadmin
      if (profile.role === 'superadmin') {
          return { error: { message: 'Impossible de supprimer un superadmin' } };
      }

      // Appeler l'Edge Function Supabase qui utilise la clé de service
      try {
          const body: { userId: string; reassignToUserId?: string } = { userId };
          if (reassignToUserId) {
              body.reassignToUserId = reassignToUserId;
          }

          const { data, error } = await supabase.functions.invoke('delete-user', { body });

          const responsePayload = data as {
              success?: boolean;
              error?: string;
              code?: string;
              dependencies?: UserDeletionDependencies;
              accessLevel?: string;
              role?: string;
          } | null;

          if (error) {
              const ctxBody = (error as { context?: { body?: string } }).context?.body;
              if (typeof ctxBody === 'string') {
                  try {
                      const parsed = JSON.parse(ctxBody) as typeof responsePayload & { dependencies?: UserDeletionDependencies };
                      if (parsed?.code === 'REASSIGNMENT_REQUIRED') {
                          return {
                              error: {
                                  message: parsed.error || 'Réaffectation requise avant suppression',
                                  code: 'REASSIGNMENT_REQUIRED',
                              },
                              dependencies: parsed.dependencies,
                          };
                      }
                      if (parsed?.error) {
                          return { error: { message: parsed.error, code: parsed.code } };
                      }
                  } catch {
                      // ignore JSON parse errors
                  }
              }
              return { error: { message: error.message || 'Erreur lors de la suppression' } };
          }

          if (!responsePayload?.success) {
              if (responsePayload?.code === 'REASSIGNMENT_REQUIRED' && responsePayload?.dependencies) {
                  return {
                      error: {
                          message: responsePayload.error || 'Réaffectation requise avant suppression',
                          code: 'REASSIGNMENT_REQUIRED',
                      },
                      dependencies: responsePayload.dependencies,
                  };
              }
              return {
                  error: {
                      message: responsePayload?.error || 'Erreur lors de la suppression',
                      code: responsePayload?.code,
                  },
              };
          }

          // Réduire l'abonnement Stripe (sauf superadmin et prestataires marketing)
          if (
              responsePayload?.accessLevel &&
              responsePayload.role !== 'superadmin' &&
              responsePayload.role !== 'prestataire_marketing'
          ) {
              try {
                  const stripeResult = await removeSubscriptionItem(
                      responsePayload.accessLevel,
                      profile.email,
                      `${profile.firstName} ${profile.lastName}`
                  );
                  if (!stripeResult.success) {
                      console.error('Erreur lors de la réduction de l\'abonnement Stripe:', stripeResult.error);
                      // On continue même si Stripe échoue (mode dégradé)
                  }
              } catch (stripeError: any) {
                  console.error('Erreur lors de la facturation Stripe:', stripeError);
                  // Mode dégradé: on continue même si Stripe échoue
              }
          }

          return { error: null };
      } catch (error: any) {
          console.error('Error calling delete-user function:', error);
          return { error: { message: error.message || 'Erreur lors de l\'appel à la fonction de suppression' } };
      }
  },

  syncUserAgencies: async (userId: string, agencyIds: string[]) => {
      // Récupérer les anciennes agences pour l'audit
      const { data: oldAgencies } = await supabase.from('user_agencies').select('*').eq('user_id', userId);
      
      const { error: delErr } = await supabase.from('user_agencies').delete().eq('user_id', userId);
      if (delErr) return { error: delErr };
      
      if (agencyIds && agencyIds.length > 0) {
          const { error: insErr } = await supabase.from('user_agencies').insert(
              agencyIds.map(aid => ({ user_id: userId, agency_id: aid }))
          );
          if (insErr) return { error: insErr };
      }
      
      // Logger l'audit
      const oldData = oldAgencies || [];
      const newData = agencyIds.map(aid => ({ user_id: userId, agency_id: aid }));
      await auditLogger.logUpdate('user_agencies', userId, oldData, newData);
      
      return { error: null };
  },
  
  getOwners: async (agencyId?: string | null): Promise<Owner[]> => {
    let query = supabase.from('owners').select('*').order('created_at', { ascending: false });
    if (agencyId) query = query.eq('agency_id', agencyId);
    
    const { data, error } = await query;
    if (error) { console.error("getOwners error:", error); return []; }
    
    return data?.map((d:any) => ({
        id: d.id,
        agencyId: d.agency_id,
        civility: d.civility, // Mapped
        firstName: d.first_name || '',
        lastName: d.last_name || '',
        address: d.address || '', // Mapped
        city: d.city || '',
        zipCode: d.zip_code || '',
        email: d.email || '',
        phone: d.phone || '',
        source: d.source,
        newsletterOptin: d.newsletter_optin || false,
        created_at: d.created_at
    })) || [];
  },

  saveOwner: async (owner: Owner) => {
    const dbOwner = {
        id: owner.id,
        civility: owner.civility,
        first_name: owner.firstName,
        last_name: owner.lastName,
        address: owner.address,
        city: owner.city,
        zip_code: owner.zipCode,
        email: owner.email,
        phone: owner.phone,
        source: owner.source,
        agency_id: owner.agencyId || null,
        newsletter_optin: owner.newsletterOptin || false
    };
    
    // Récupérer l'ancien owner si c'est une mise à jour
    let oldOwner: any = null;
    if (dbOwner.id) {
      const { data: existingOwner } = await supabase.from('owners').select('*').eq('id', dbOwner.id).single();
      if (existingOwner) oldOwner = existingOwner;
    }
    
    if (!dbOwner.id) delete (dbOwner as any).id;

    const { data, error } = await supabase.from('owners').upsert(dbOwner).select().single();
    if (error) console.error('Error saving owner:', error);
    
    // Logger l'audit
    if (data && !error) {
      const actionType = oldOwner ? 'UPDATE' : 'INSERT';
      await auditLogger.log(actionType, 'owners', data.id, oldOwner, dbOwner);
    }
    
    if (data) {
        return { data: {
            ...owner,
            id: data.id,
            agencyId: data.agency_id
        }, error: null };
    }
    return { data: null, error };
  },

  deleteOwner: async (id: string) => {
    // Récupérer l'owner avant suppression pour l'audit
    const { data: oldOwner } = await supabase.from('owners').select('*').eq('id', id).single();
    
    const { error } = await supabase.from('owners').delete().eq('id', id);
    
    // Logger l'audit
    if (!error && oldOwner) {
      await auditLogger.logDelete('owners', id, oldOwner);
    }
    
    return { error };
  },

  getOwnersWithNewsletterOptin: async (): Promise<Owner[]> => {
    const { data, error } = await supabase
      .from('owners')
      .select('*')
      .eq('newsletter_optin', true)
      .order('created_at', { ascending: false });
    
    if (error) { 
      console.error("getOwnersWithNewsletterOptin error:", error); 
      return []; 
    }
    
    return data?.map((d:any) => ({
        id: d.id,
        agencyId: d.agency_id,
        civility: d.civility,
        firstName: d.first_name || '',
        lastName: d.last_name || '',
        address: d.address || '',
        city: d.city || '',
        zipCode: d.zip_code || '',
        email: d.email || '',
        phone: d.phone || '',
        source: d.source,
        newsletterOptin: d.newsletter_optin || false,
        created_at: d.created_at
    })) || [];
  },

  getProperties: async (agencyId?: string | null): Promise<Property[]> => {
    let query = supabase.from('properties').select('*');
    if (agencyId) query = query.eq('agency_id', agencyId);
    
    const { data, error } = await query;
    if (error) return [];
    
    return data?.map((d:any) => ({
        id: d.id,
        agencyId: d.agency_id,
        ownerId: d.owner_id,
        address: d.address,
        city: d.city,
        zipCode: d.zip_code,
        type: d.type,
        surface: d.surface,
        bedrooms: d.bedrooms,
        bathrooms: d.bathrooms,
        maxGuests: d.max_guests,
        created_at: d.created_at,
        // Managed Fields - stored in a JSON column 'details' in real DB
        name: d.details?.name,
        status: d.details?.status || 'PROSPECT',
        contractType: d.details?.contractType,
        superhoteId: d.details?.superhoteId,
        assets: d.details?.assets,
        checklist: d.details?.checklist,
        documents: d.details?.documents,
        internalNotes: d.details?.internalNotes,
        // Facturation propriétaire (real columns)
        rrRentalId: d.rr_rental_id,
        rrRentalName: d.rr_rental_name,
        commissionRateOverride: d.commission_rate_override,
        rebillCleaning: d.rebill_cleaning || false,
        cityTaxRules: d.city_tax_rules || {}
    })) || [];
  },

  saveProperty: async (property: Property) => {
    const dbProp = {
        id: property.id,
        owner_id: property.ownerId,
        address: property.address,
        city: property.city,
        zip_code: property.zipCode,
        type: property.type,
        surface: property.surface,
        bedrooms: property.bedrooms,
        bathrooms: property.bathrooms,
        max_guests: property.maxGuests,
        agency_id: property.agencyId || null,
        // Facturation propriétaire (real columns; undefined keys are dropped by
        // JSON serialization so an update that doesn't carry them leaves them untouched)
        rr_rental_id: property.rrRentalId,
        rr_rental_name: property.rrRentalName,
        commission_rate_override: property.commissionRateOverride,
        rebill_cleaning: property.rebillCleaning,
        city_tax_rules: property.cityTaxRules,
        // JSON field for extra data
        details: {
            name: property.name,
            status: property.status || 'PROSPECT',
            contractType: property.contractType,
            superhoteId: property.superhoteId,
            assets: property.assets,
            checklist: property.checklist,
            documents: property.documents,
            internalNotes: property.internalNotes
        }
    };
    
    // Récupérer l'ancienne property si c'est une mise à jour
    let oldProperty: any = null;
    if (dbProp.id) {
      const { data: existingProperty } = await supabase.from('properties').select('*').eq('id', dbProp.id).single();
      if (existingProperty) oldProperty = existingProperty;
    }
    
    if (!dbProp.id) delete (dbProp as any).id;

    const { data, error } = await supabase.from('properties').upsert(dbProp).select().single();
    if (error) console.error('Error saving property:', error);
    
    // Logger l'audit
    if (data && !error) {
      const actionType = oldProperty ? 'UPDATE' : 'INSERT';
      await auditLogger.log(actionType, 'properties', data.id, oldProperty, dbProp);
    }
    
    if (data) {
        return { data: {
            ...property,
            id: data.id
        }, error: null };
    }
    return { data: null, error };
  },

  // NEW: Upload document to storage bucket AND return path
  uploadPropertyDocument: async (file: File | Blob, propertyId: string, customName?: string) => {
      // Clean name
      const safeName = (customName || (file as File).name || 'document').replace(/[^a-zA-Z0-9.]/g, '_');
      const fileName = `${propertyId}/${Date.now()}_${safeName}`;
      
      const { data, error } = await supabase.storage
          .from('property-documents') // Assumes bucket exists (created via SQL)
          .upload(fileName, file, {
              cacheControl: '3600',
              upsert: false,
              contentType: 'application/pdf' // Force PDF for signed contracts mostly
          });
      
      if (error) {
          console.error("Upload Error:", error);
          throw error;
      }

      // Return only the path (fileName) so we can generate fresh signed URLs on demand
      return fileName;
  },

  // NEW: Generate a signed URL for a file path (valid 1h)
  getFileUrl: async (path: string) => {
      // 3600 seconds = 1 hour validity
      const { data, error } = await supabase.storage
          .from('property-documents')
          .createSignedUrl(path, 3600);
      
      if (error) {
          console.error("Error creating signed URL:", error);
          return null;
      }
      return data.signedUrl;
  },

  // NEW: Delete a file from storage bucket
  deletePropertyDocument: async (path: string) => {
      const { error } = await supabase.storage
          .from('property-documents')
          .remove([path]);
      
      return { error };
  },

  // Supprimer tous les documents d'un bien depuis le Storage
  deleteAllPropertyDocuments: async (propertyId: string) => {
      try {
          // Lister tous les fichiers dans le dossier {propertyId}/
          const { data: files, error: listError } = await supabase.storage
              .from('property-documents')
              .list(propertyId);
          
          if (listError) {
              console.error("Error listing property documents:", listError);
              return { count: 0, error: listError };
          }
          
          if (!files || files.length === 0) {
              return { count: 0, error: null };
          }
          
          // Construire les chemins complets
          const paths = files.map(file => `${propertyId}/${file.name}`);
          
          // Supprimer tous les fichiers
          const { error: deleteError } = await supabase.storage
              .from('property-documents')
              .remove(paths);
          
          if (deleteError) {
              console.error("Error deleting property documents:", deleteError);
              return { count: 0, error: deleteError };
          }
          
          return { count: files.length, error: null };
      } catch (error: any) {
          console.error("Unexpected error in deleteAllPropertyDocuments:", error);
          return { count: 0, error };
      }
  },

  // Évaluer l'impact de la suppression d'un bien
  getPropertyDeletionImpact: async (propertyId: string) => {
      try {
          // Compter les deals liés
          const { data: deals, error: dealsError } = await supabase
              .from('deals')
              .select('id')
              .eq('property_id', propertyId);
          
          if (dealsError) {
              console.error("Error counting deals:", dealsError);
          }
          
          const dealsCount = deals?.length || 0;
          const dealIds = deals?.map(d => d.id) || [];
          
          // Compter les contrats liés (via deals)
          let contractsCount = 0;
          if (dealIds.length > 0) {
              const { count: contractsCountResult, error: contractsError } = await supabase
                  .from('contracts')
                  .select('*', { count: 'exact', head: true })
                  .in('deal_id', dealIds);
              
              if (contractsError) {
                  console.error("Error counting contracts:", contractsError);
              } else {
                  contractsCount = contractsCountResult || 0;
              }
          }
          
          // Compter les propositions liées (via deals)
          let proposalsCount = 0;
          if (dealIds.length > 0) {
              const { count: proposalsCountResult, error: proposalsError } = await supabase
                  .from('proposals')
                  .select('*', { count: 'exact', head: true })
                  .in('deal_id', dealIds);
              
              if (proposalsError) {
                  console.error("Error counting proposals:", proposalsError);
              } else {
                  proposalsCount = proposalsCountResult || 0;
              }
          }
          
          // Compter les documents dans Storage
          const { data: files, error: filesError } = await supabase.storage
              .from('property-documents')
              .list(propertyId);
          
          const documentsCount = filesError ? 0 : (files?.length || 0);
          
          // Compter les dépenses
          const { count: expensesCount, error: expensesError } = await supabase
              .from('expenses')
              .select('*', { count: 'exact', head: true })
              .eq('property_id', propertyId);
          
          if (expensesError) {
              console.error("Error counting expenses:", expensesError);
          }
          
          // Compter les revenus
          const { count: revenueCount, error: revenueError } = await supabase
              .from('revenue')
              .select('*', { count: 'exact', head: true })
              .eq('property_id', propertyId);
          
          if (revenueError) {
              console.error("Error counting revenue:", revenueError);
          }
          
          return {
              dealsCount,
              contractsCount,
              proposalsCount,
              documentsCount,
              expensesCount: expensesCount || 0,
              revenueCount: revenueCount || 0,
              error: null
          };
      } catch (error: any) {
          console.error("Unexpected error in getPropertyDeletionImpact:", error);
          return {
              dealsCount: 0,
              contractsCount: 0,
              proposalsCount: 0,
              documentsCount: 0,
              expensesCount: 0,
              revenueCount: 0,
              error
          };
      }
  },

  deleteProperty: async (id: string) => {
    // Récupérer la property avant suppression pour l'audit
    const { data: oldProperty } = await supabase.from('properties').select('*').eq('id', id).single();
    
    if (!oldProperty) {
        return { error: { message: 'Property not found' } };
    }
    
    // Récupérer les deals liés pour les délier
    const { data: linkedDeals, error: dealsFetchError } = await supabase
        .from('deals')
        .select('id')
        .eq('property_id', id);
    
    if (dealsFetchError) {
        console.error("Error fetching linked deals:", dealsFetchError);
    }
    
    const dealsCount = linkedDeals?.length || 0;
    
    // Délier les deals en mettant property_id à NULL
    if (dealsCount > 0 && linkedDeals) {
        const dealIds = linkedDeals.map(d => d.id);
        const { error: unlinkError } = await supabase
            .from('deals')
            .update({ property_id: null })
            .in('id', dealIds);
        
        if (unlinkError) {
            console.error("Error unlinking deals:", unlinkError);
            return { error: unlinkError };
        }
    }
    
    // Supprimer tous les documents du Storage
    // Note: On doit appeler la fonction directement car on est dans le même objet
    let documentsDeleted = 0;
    try {
        // Lister tous les fichiers dans le dossier {propertyId}/
        const { data: files, error: listError } = await supabase.storage
            .from('property-documents')
            .list(id);
        
        if (!listError && files && files.length > 0) {
            // Construire les chemins complets
            const paths = files.map(file => `${id}/${file.name}`);
            
            // Supprimer tous les fichiers
            const { error: deleteError } = await supabase.storage
                .from('property-documents')
                .remove(paths);
            
            if (!deleteError) {
                documentsDeleted = files.length;
            } else {
                console.error("Error deleting property documents:", deleteError);
            }
        }
    } catch (error: any) {
        console.error("Unexpected error deleting property documents:", error);
        // On continue quand même la suppression du bien
    }
    
    // Supprimer le bien (CASCADE supprimera automatiquement expenses et revenue)
    const { error } = await supabase.from('properties').delete().eq('id', id);
    
    // Logger l'audit
    if (!error && oldProperty) {
      await auditLogger.logDelete('properties', id, oldProperty);
    }
    
    return { 
        error,
        deletedDocuments: documentsDeleted,
        linkedDeals: dealsCount
    };
  },

  // Retirer le statut MANAGED d'un bien (passer à PROSPECT)
  removeManagedStatus: async (id: string) => {
    // Récupérer la property avant modification pour l'audit
    const { data: oldProperty } = await supabase.from('properties').select('*').eq('id', id).single();
    
    if (!oldProperty) {
        return { error: { message: 'Property not found' } };
    }
    
    // Le statut est stocké dans le champ JSON details
    const currentDetails = oldProperty.details || {};
    const updatedDetails = {
        ...currentDetails,
        status: 'ARCHIVED'
    };
    
    // Mettre à jour le statut dans details
    const { data, error } = await supabase
        .from('properties')
        .update({ details: updatedDetails })
        .eq('id', id)
        .select()
        .single();
    
    // Logger l'audit
    if (data && !error && oldProperty) {
        const updatedProperty = { ...oldProperty, details: updatedDetails };
        await auditLogger.logUpdate('properties', id, oldProperty, updatedProperty);
    }
    
    return { data, error };
  },

  getDeals: async (agencyId?: string | null): Promise<Deal[]> => {
    let query = supabase.from('deals').select('*');
    if (agencyId) query = query.eq('agency_id', agencyId);
    
    const { data, error } = await query;
    if (error) return [];
    
    return data?.map((d: any) => ({
        id: d.id,
        propertyId: d.property_id,
        ownerId: d.owner_id,
        type: d.type,
        stage: d.stage,
        value: d.value,
        monthlyRentWithCharges: d.monthly_rent_with_charges,
        source: d.source,
        notes: d.notes || [],
        reminders: d.reminders || [],
        sequenceSteps: d.sequence_steps || [],
        createdAt: d.created_at,
        agencyId: d.agency_id,
        proposalId: d.proposal_id,
        contractId: d.contract_id, // Mapped
        needsCompletion: d.needs_completion || false, // Mapped
        marketingProviderId: d.marketing_provider_id || null, // Mapped
        marketingLeadId: d.marketing_lead_id || null // Mapped
    })) || [];
  },

  saveDeal: async (deal: Deal): Promise<{ data: Deal | null; error: any }> => {
    const normalizedReminders = (deal.reminders || []).map(normalizeReminderForSync);
    const dbDeal = {
        id: deal.id,
        property_id: deal.propertyId,
        owner_id: deal.ownerId,
        type: deal.type,
        stage: deal.stage,
        value: deal.value,
        monthly_rent_with_charges: deal.monthlyRentWithCharges || null,
        source: deal.source,
        notes: deal.notes,
        reminders: normalizedReminders,
        sequence_steps: deal.sequenceSteps || [], // FORCE EMPTY ARRAY IF UNDEFINED TO ENSURE JSON TYPE
        agency_id: deal.agencyId || null,
        proposal_id: deal.proposalId || null,
        contract_id: deal.contractId || null,
        needs_completion: deal.needsCompletion || false,
        marketing_provider_id: deal.marketingProviderId || null,
        marketing_lead_id: deal.marketingLeadId || null
    };

    // Récupérer l'ancien deal si c'est une mise à jour
    let oldDeal: any = null;
    if (dbDeal.id) {
      const { data: existingDeal } = await supabase.from('deals').select('*').eq('id', dbDeal.id).single();
      if (existingDeal) oldDeal = existingDeal;
    }

    if (!dbDeal.id) delete (dbDeal as any).id;

    const { data, error } = await supabase.from('deals').upsert(dbDeal).select().single();
    if (error) {
      console.error('Error saving deal:', error);
      return { data: null, error };
    }

    // Logger l'audit
    if (data && !error) {
      const actionType = oldDeal ? 'UPDATE' : 'INSERT';
      await auditLogger.log(actionType, 'deals', data.id, oldDeal, dbDeal);
    }

    // Sync sortante Google Calendar (non bloquante)
    try {
      if (data?.id) {
        await googleCalendarService.syncOutbound(
          'deal',
          data.id,
          (oldDeal?.reminders || []) as Reminder[],
          normalizedReminders,
        );
      }
    } catch (syncError) {
      console.error('Google Calendar outbound sync failed for deal', syncError);
    }

    if (!data) return { data: null, error: null };

    const mappedDeal: Deal = {
      id: data.id,
      agencyId: data.agency_id,
      propertyId: data.property_id,
      ownerId: data.owner_id,
      type: data.type as PipelineType,
      stage: data.stage as DealStage,
      value: data.value,
      monthlyRentWithCharges: data.monthly_rent_with_charges,
      source: data.source as LeadSource,
      notes: data.notes,
      reminders: data.reminders,
      sequenceSteps: data.sequence_steps,
      createdAt: data.created_at,
      proposalId: data.proposal_id,
      contractId: data.contract_id,
      needsCompletion: data.needs_completion,
      marketingProviderId: data.marketing_provider_id,
      marketingLeadId: data.marketing_lead_id
    };

    return { data: mappedDeal, error: null };
  },

  deleteDeal: async (id: string) => {
    // Récupérer le deal avant suppression pour l'audit
    const { data: oldDeal } = await supabase.from('deals').select('*').eq('id', id).single();
    
    const { error } = await supabase.from('deals').delete().eq('id', id);
    
    // Logger l'audit
    if (!error && oldDeal) {
      await auditLogger.logDelete('deals', id, oldDeal);
    }
    
    return { error };
  },

  createFullDeal: async (deal: Deal, owner: Owner, property: Property, agencyId: string | null) => {
      try {
          const ownerPayload = { ...owner, agencyId };
          const { data: ownerData, error: ownerError } = await storage.saveOwner(ownerPayload);
          if (ownerError || !ownerData) throw new Error("Erreur création propriétaire: " + (ownerError?.message || 'Unknown'));
          
          const newOwnerId = ownerData.id!;

          const propertyPayload = { ...property, ownerId: newOwnerId, agencyId };
          if (!propertyPayload.type) propertyPayload.type = 'Appartement';
          
          const { data: propData, error: propError } = await storage.saveProperty(propertyPayload);
          if (propError || !propData) throw new Error("Erreur création bien: " + (propError?.message || 'Unknown'));

          const newPropId = propData.id!;

          const dealPayload = { 
              ...deal, 
              ownerId: newOwnerId, 
              propertyId: newPropId, 
              agencyId 
          };
          const { error: dealSaveError } = await storage.saveDeal(dealPayload);
          if (dealSaveError) throw new Error("Erreur création deal: " + (dealSaveError?.message || 'Unknown'));

          return { success: true };
      } catch (e) {
          console.error("Unified Creation Error:", e);
          return { error: e };
      }
  },

  // --- CSV IMPORT ---
  importLeadsFromCSV: async (
    csvContent: string, 
    agencyId: string | null, // OBLIGATOIRE maintenant
    options?: { skipDuplicates?: boolean }
  ): Promise<{
    success: number;
    errors: number;
    duplicates: number;
    details: Array<{ row: number; success: boolean; error?: string; duplicate?: boolean }>;
  }> => {
    const results = {
      success: 0,
      errors: 0,
      duplicates: 0,
      details: [] as Array<{ row: number; success: boolean; error?: string; duplicate?: boolean }>
    };

    try {
      // Vérifier qu'une agence est fournie
      if (!agencyId) {
        throw new Error('Une agence doit être sélectionnée pour importer des leads');
      }

      // Parser le CSV
      const rows = parseCSV(csvContent);
      
      if (rows.length === 0) {
        return results;
      }

      // Mapper les statuts CSV vers DealStage
      const mapStatus = (statut: string): DealStage => {
        const statutLower = statut.toLowerCase();
        if (statutLower.includes('perdu')) return DealStage.LOST;
        if (statutLower.includes('qualifié')) return DealStage.QUALIFIED;
        if (statutLower.includes('gagné') || statutLower.includes('contrat signé')) return DealStage.SIGNED;
        if (statutLower.includes('prospect')) return DealStage.NEW;
        return DealStage.NEW; // Par défaut
      };

      // Mapper les sources CSV vers LeadSource
      const mapSource = (source: string): LeadSource => {
        const sourceLower = source.toLowerCase();
        if (sourceLower.includes('leboncoin')) return 'Leboncoin';
        if (sourceLower.includes('créé manuellement') || sourceLower.includes('manuellement')) return 'Autre';
        return 'Autre'; // Par défaut
      };

      // Traiter chaque ligne
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const leadRow = mapCSVRowToLead(row);
        const rowNumber = i + 2; // +2 car ligne 1 = headers, et index 0-based

        try {
          // Validation minimale : au moins un nom ou email/téléphone
          if (!leadRow.nomDuLead && !leadRow.email && !leadRow.telephone) {
            results.errors++;
            results.details.push({
              row: rowNumber,
              success: false,
              error: 'Données insuffisantes (nom, email ou téléphone requis)'
            });
            continue;
          }

          // Parser les données
          const { firstName, lastName } = parseFullName(leadRow.nomDuLead);
          const email = normalizeEmail(leadRow.email);
          const phone = normalizePhone(leadRow.telephone);
          const { address, city, zipCode } = parseAddress(leadRow.ville);
          const source = mapSource(leadRow.source);
          const stage = mapStatus(leadRow.statut);
          const createdAt = parseFrenchDate(leadRow.dateCreation || leadRow.dateAjoutLead);
          const leboncoinUrl = extractLeboncoinUrl(leadRow.piecesJointes);

          // Vérifier les doublons (par email ou téléphone)
          let existingOwner: Owner | null = null;
          if (email) {
            const { data: ownersByEmail } = await supabase
              .from('owners')
              .select('*')
              .eq('email', email)
              .limit(1);
            if (ownersByEmail && ownersByEmail.length > 0) {
              existingOwner = {
                id: ownersByEmail[0].id,
                firstName: ownersByEmail[0].first_name,
                lastName: ownersByEmail[0].last_name,
                email: ownersByEmail[0].email,
                phone: ownersByEmail[0].phone,
                agencyId: ownersByEmail[0].agency_id,
                source: ownersByEmail[0].source as LeadSource
              };
            }
          }

          if (!existingOwner && phone) {
            const { data: ownersByPhone } = await supabase
              .from('owners')
              .select('*')
              .eq('phone', phone)
              .limit(1);
            if (ownersByPhone && ownersByPhone.length > 0) {
              existingOwner = {
                id: ownersByPhone[0].id,
                firstName: ownersByPhone[0].first_name,
                lastName: ownersByPhone[0].last_name,
                email: ownersByPhone[0].email,
                phone: ownersByPhone[0].phone,
                agencyId: ownersByPhone[0].agency_id,
                source: ownersByPhone[0].source as LeadSource
              };
            }
          }

          // Gérer les doublons
          if (existingOwner && options?.skipDuplicates) {
            results.duplicates++;
            results.details.push({
              row: rowNumber,
              success: false,
              duplicate: true,
              error: 'Doublon ignoré (owner existant)'
            });
            continue;
          }

          // Créer ou utiliser owner existant
          let ownerId: string;
          if (existingOwner) {
            ownerId = existingOwner.id!;
            results.duplicates++;
          } else {
          const owner: Owner = {
            firstName: firstName || 'A définir',
            lastName: lastName || '',
            email: email || '',
            phone: phone || '',
            source: source,
            agencyId: agencyId // OBLIGATOIRE maintenant
          };

            const { data: ownerData, error: ownerError } = await storage.saveOwner(owner);
            if (ownerError || !ownerData) {
              throw new Error(`Erreur création owner: ${ownerError?.message || 'Unknown'}`);
            }
            ownerId = ownerData.id!;
          }

          // Créer property
          const property: Property = {
            ownerId: ownerId,
            address: address || city || 'Adresse à compléter',
            city: city || 'Ville à compléter',
            zipCode: zipCode,
            type: 'Appartement',
            surface: 0, // À compléter
            status: 'PROSPECT',
            agencyId: agencyId // OBLIGATOIRE maintenant
          };

          const { data: propData, error: propError } = await storage.saveProperty(property);
          if (propError || !propData) {
            throw new Error(`Erreur création property: ${propError?.message || 'Unknown'}`);
          }
          const propertyId = propData.id!;

          // Créer note avec historique
          let noteContent = '📋 Import depuis ancien CRM\n';
          if (leadRow.dateCreation) {
            noteContent += `📅 Date création : ${leadRow.dateCreation}\n`;
          }
          if (leboncoinUrl) {
            noteContent += `🔗 Annonce Leboncoin: ${leboncoinUrl}\n`;
          }
          if (leadRow.historique) {
            noteContent += `\n📝 Historique :\n${leadRow.historique}`;
          }

          const notes = [{
            id: Date.now().toString(),
            content: noteContent,
            createdAt: new Date().toISOString(),
            authorName: 'Système'
          }];

          // Créer deal
          const deal: Deal = {
            propertyId: propertyId,
            ownerId: ownerId,
            type: PipelineType.SUBLET, // Par défaut sous-location
            stage: stage,
            value: 0, // À compléter
            source: source,
            notes: notes,
            needsCompletion: true, // Données incomplètes
            agencyId: agencyId // OBLIGATOIRE maintenant
          };

          if (createdAt) {
            // Note: createdAt n'est pas directement modifiable dans saveDeal, 
            // mais on peut l'ajouter via raw SQL si nécessaire
            // Pour l'instant, on laisse la DB gérer la date
          }

          const { error: dealError } = await storage.saveDeal(deal);
          if (dealError) {
            throw new Error(`Erreur création deal: ${dealError?.message || 'Unknown'}`);
          }

          results.success++;
          results.details.push({
            row: rowNumber,
            success: true
          });

        } catch (error: any) {
          results.errors++;
          results.details.push({
            row: rowNumber,
            success: false,
            error: error.message || 'Erreur inconnue'
          });
          console.error(`Erreur import ligne ${rowNumber}:`, error);
        }
      }

      return results;
    } catch (error: any) {
      console.error('Erreur parsing CSV:', error);
      return {
        ...results,
        errors: results.errors + 1,
        details: [{
          row: 0,
          success: false,
          error: `Erreur parsing CSV: ${error.message || 'Unknown'}`
        }]
      };
    }
  },

  // --- PROPOSALS CRUD ---
  getProposals: async (): Promise<Proposal[]> => {
      const { data, error } = await supabase.from('proposals').select('*').order('created_at', { ascending: false });
      if (error) {
          console.error('Error fetching proposals:', error);
          return [];
      }
      return data?.map((p: any) => ({
          id: p.id,
          dealId: p.deal_id,
          reference: p.reference, // Mapped
          createdAt: p.created_at,
          status: p.status,
          nightlyRate: p.nightly_rate,
          occupancyRate: p.occupancy_rate,
          commissionRate: p.commission_rate,
          cleaningFee: p.cleaning_fee,
          setupFee: p.setup_fee,
          isSetupFeeOffered: p.is_setup_fee_offered,
          estimatedMonthlyRevenue: p.estimated_monthly_revenue,
          estimatedAnnualRevenue: p.estimated_annual_revenue,
          estimatedNetOwner: p.estimated_net_owner
      })) || [];
  },

  saveProposal: async (proposal: Proposal) => {
      const dbProposal = {
          id: proposal.id,
          deal_id: proposal.dealId,
          reference: proposal.reference, // Mapped
          created_at: proposal.createdAt,
          status: proposal.status,
          nightly_rate: proposal.nightlyRate,
          occup_rate: proposal.occupancyRate, // Note: check DB column name (occupancy_rate in schema usually)
          commission_rate: proposal.commissionRate,
          cleaning_fee: proposal.cleaningFee,
          setup_fee: proposal.setupFee,
          is_setup_fee_offered: proposal.isSetupFeeOffered,
          estimated_monthly_revenue: proposal.estimatedMonthlyRevenue,
          estimated_annual_revenue: proposal.estimatedAnnualRevenue,
          estimated_net_owner: proposal.estimatedNetOwner
      };
      
      // Fix potential typo in column mapping if necessary, assuming snake_case in DB
      const dbPayload: any = { ...dbProposal, occupancy_rate: proposal.occupancyRate };
      delete dbPayload.occup_rate;

      // Récupérer l'ancienne proposal si c'est une mise à jour
      let oldProposal: any = null;
      if (dbPayload.id) {
        const { data: existingProposal } = await supabase.from('proposals').select('*').eq('id', dbPayload.id).single();
        if (existingProposal) oldProposal = existingProposal;
      }

      const { error } = await supabase.from('proposals').upsert(dbPayload).select().single();
      if (error) console.error('Error saving proposal:', error);
      
      // Logger l'audit
      if (!error && dbPayload.id) {
        const actionType = oldProposal ? 'UPDATE' : 'INSERT';
        await auditLogger.log(actionType, 'proposals', dbPayload.id, oldProposal, dbPayload);
      }
      
      return { error };
  },

  // Helper to mark proposal sent and update deal
  markProposalSent: async (proposal: Proposal, deal: Deal, sentByEmail: boolean = false) => {
      // 1. Update Proposal Status
      const updatedProposal = { ...proposal, status: 'SENT' as const };
      await storage.saveProposal(updatedProposal);

      // 2. Prepare Deal Updates
      const today = new Date();
      const nextFollowUp = new Date(today);
      nextFollowUp.setDate(today.getDate() + 3);
      if (nextFollowUp.getDay() === 0) nextFollowUp.setDate(nextFollowUp.getDate() + 1); // If Sunday, move to Monday

      const method = sentByEmail ? "Envoyé par email" : "Envoyé manuellement";
      const newNote = {
          id: Date.now().toString(),
          content: `📄 Proposition commerciale envoyée (${method}).`,
          createdAt: new Date().toISOString(),
          authorName: 'Système'
      };

      const newReminder = {
          id: Date.now().toString(),
          date: nextFollowUp.toISOString(),
          note: "Relancer proposition commerciale",
          isDone: false
      };

      // 3. Update Deal
      const updatedDeal = {
          ...deal,
          stage: DealStage.PROPOSAL_SENT,
          proposalId: proposal.id,
          notes: [...(deal.notes || []), newNote],
          reminders: [...(deal.reminders || []), newReminder]
      };

      await storage.saveDeal(updatedDeal);
      return { proposal: updatedProposal, deal: updatedDeal };
  },

  // Mark proposal as accepted, BUT NOT SIGNED YET (Wait for Contract)
  acceptProposal: async (proposal: Proposal, deal: Deal, property: Property) => {
      // 1. Update Proposal Status
      const updatedProposal = { ...proposal, status: 'ACCEPTED' as const };
      
      const { error: propError } = await storage.saveProposal(updatedProposal);
      if (propError) throw propError;

      // 2. Update Deal to PROPOSAL_ACCEPTED (Ready for contract)
      const acceptNote = {
          id: Date.now().toString(),
          content: `✅ Proposition ${proposal.reference} VALIDÉE par le client. Prochaine étape : Contrat.`,
          createdAt: new Date().toISOString(),
          authorName: 'Système'
      };
      
      const updatedDeal = {
          ...deal,
          stage: DealStage.PROPOSAL_ACCEPTED,
          proposalId: proposal.id, // Ensure link
          notes: [...(deal.notes || []), acceptNote]
      };
      await storage.saveDeal(updatedDeal);

      // Property remains PROSPECT until contract signed
      return { proposal: updatedProposal, deal: updatedDeal };
  },

  // --- CONTRACTS CRUD ---
  getContracts: async (): Promise<Contract[]> => {
      const { data, error } = await supabase.from('contracts').select('*').order('created_at', { ascending: false });
      if (error) { console.error('Error fetching contracts', error); return []; }
      
      return data?.map((c: any) => ({
          id: c.id,
          dealId: c.deal_id,
          proposalId: c.proposal_id,
          type: c.type,
          status: c.status,
          contractNumber: c.contract_number, // Mapped
          createdAt: c.created_at,
          signedAt: c.signed_at,
          startDate: c.start_date,
          duration: c.duration,
          commissionRate: c.commission_rate,
          rentAmount: c.rent_amount,
          // NEW FIELDS MAPPING
          signedCity: c.signed_city,
          signedDate: c.signed_date,
          // DB column is yousign_request_id (short name)
          yousignSignatureRequestId: c.yousign_request_id || c.yousign_signature_request_id, 
          yousignStatus: c.yousign_status, // Mapped
          // Supabase JSONB returns objects directly, but handle string case for safety
          contentJson: c.content_json 
              ? (typeof c.content_json === 'string' ? JSON.parse(c.content_json) : c.content_json)
              : undefined
      })) || [];
  },

  saveContract: async (contract: Contract) => {
      // Récupérer le marketing_provider_id depuis le deal si non fourni
      let marketingProviderId = contract.marketingProviderId;
      if (!marketingProviderId && contract.dealId) {
        const { data: dealData } = await supabase.from('deals').select('marketing_provider_id').eq('id', contract.dealId).single();
        if (dealData?.marketing_provider_id) {
          marketingProviderId = dealData.marketing_provider_id;
        }
      }

      const dbContract = {
          id: contract.id,
          deal_id: contract.dealId,
          proposal_id: contract.proposalId,
          type: contract.type,
          status: contract.status,
          contract_number: contract.contractNumber, // Mapped
          created_at: contract.createdAt,
          signed_at: contract.signedAt,
          start_date: contract.startDate,
          duration: contract.duration,
          commission_rate: contract.commissionRate,
          rent_amount: contract.rentAmount,
          // NEW FIELDS MAPPING
          signed_city: contract.signedCity,
          signed_date: contract.signedDate,
          // FIX: Use the correct DB column name (yousign_request_id)
          yousign_request_id: contract.yousignSignatureRequestId,
          yousign_status: contract.yousignStatus, // Mapped
          // Supabase JSONB columns accept JavaScript objects directly, no need to stringify
          content_json: contract.contentJson || null,
          marketing_provider_id: marketingProviderId || null
      };
      
      // Récupérer l'ancien contract si c'est une mise à jour
      let oldContract: any = null;
      if (dbContract.id) {
        const { data: existingContract } = await supabase.from('contracts').select('*').eq('id', dbContract.id).single();
        if (existingContract) oldContract = existingContract;
      }
      
      const { error } = await supabase.from('contracts').upsert(dbContract).select().single();
      if (error) {
          console.error('Error saving contract:', error);
          console.error('Contract data:', JSON.stringify(dbContract, null, 2));
      }
      
      // Logger l'audit
      if (!error && dbContract.id) {
        const actionType = oldContract ? 'UPDATE' : 'INSERT';
        await auditLogger.log(actionType, 'contracts', dbContract.id, oldContract, dbContract);
      }
      
      return { error };
  },

  deleteContract: async (id: string) => {
      // Récupérer le contract avant suppression pour l'audit
      const { data: oldContract } = await supabase.from('contracts').select('*').eq('id', id).single();
      
      const { error } = await supabase.from('contracts').delete().eq('id', id);
      
      // Logger l'audit
      if (!error && oldContract) {
        await auditLogger.logDelete('contracts', id, oldContract);
      }
      
      return { error };
  },

  // Action: Sign Contract and Close Deal
  // UPDATED: Accepts custom Note and optional Signed File (Blob)
  signContract: async (
      contract: Contract, 
      deal: Deal, 
      property: Property, 
      noteContent: string = "🎉 CONTRAT SIGNÉ ! Le deal est officiellement gagné.",
      signedFileBlob?: Blob
  ) => {
      // 1. Update Contract
      const signedContract = { ...contract, status: 'SIGNED' as const, signedAt: new Date().toISOString() };
      await storage.saveContract(signedContract);

      // 2. Upload Signed File if exists and attach to Property
      // Ensure existing documents array exists
      let updatedDocuments = property.documents || [];
      
      if (signedFileBlob && property.id) {
          try {
              const fileName = `contrat_signe_${contract.contractNumber || 'ref'}.pdf`;
              const filePath = await storage.uploadPropertyDocument(signedFileBlob, property.id, fileName);
              
              const newDoc: PropertyDocument = {
                  id: Date.now().toString(),
                  name: `Contrat Signé - ${contract.contractNumber}`,
                  type: 'pdf',
                  category: 'Mandat / Contrat', // Ensure this category exists or is handled
                  uploadDate: new Date().toISOString(),
                  path: filePath
              };
              updatedDocuments = [...updatedDocuments, newDoc];
          } catch (e) {
              console.error("Error uploading signed contract:", e);
          }
      }

      // 3. Set Property to MANAGED
      const updatedProperty = {
          ...property,
          status: 'MANAGED' as const,
          contractType: deal.type,
          documents: updatedDocuments // Explicitly set documents to ensure it's updated in the JSON
      };
      await storage.saveProperty(updatedProperty);

      // 4. Win Deal
      const winNote = {
          id: Date.now().toString(),
          content: noteContent,
          createdAt: new Date().toISOString(),
          authorName: 'Système'
      };
      
      const updatedSteps = (deal.sequenceSteps || []).map(step => 
          step.status === 'PENDING' ? { ...step, status: 'CANCELLED' as const } : step
      );

      const updatedDeal = {
          ...deal,
          stage: DealStage.SIGNED,
          contractId: contract.id,
          notes: [...(deal.notes || []), winNote],
          sequenceSteps: updatedSteps
      };
      await storage.saveDeal(updatedDeal);

      // 5. Envoyer notification au prestataire marketing si applicable
      if (contract.marketingProviderId) {
          try {
              const provider = await storage.getMarketingProvider(contract.marketingProviderId);
              if (provider) {
                  // Récupérer le propriétaire
                  const owners = await storage.getOwners();
                  const owner = owners.find(o => o.id === deal.ownerId);
                  if (owner) {
                      // Récupérer l'agence
                      const agencies = await storage.getAllAgencies();
                      const agency = agencies.find(a => a.id === deal.agencyId);
                      const agencyName = agency ? (agency.name || agency.city) : 'Agence inconnue';
                      
                      // Envoyer l'email
                      const { emailService } = await import('./email');
                      await emailService.sendContractSignedNotification(
                          provider.email,
                          signedContract,
                          updatedDeal,
                          owner,
                          agencyName
                      );
                  }
              }
          } catch (emailError) {
              console.error('Error sending contract signed notification to marketing provider', emailError);
              // Ne pas bloquer la signature si l'email échoue
          }
      }

      return { contract: signedContract, deal: updatedDeal, property: updatedProperty };
  },

  // --- Sequences Templates CRUD ---
  getSequenceTemplates: async (): Promise<SequenceTemplate[]> => {
      const { data, error } = await supabase.from('sequence_templates').select('*');
      if (error) {
          console.error("Error fetching templates", error);
          return [];
      }
      return data || [];
  },

  saveSequenceTemplate: async (template: SequenceTemplate) => {
      // Récupérer l'ancien template si c'est une mise à jour
      let oldTemplate: any = null;
      if (template.id) {
        const { data: existingTemplate } = await supabase.from('sequence_templates').select('*').eq('id', template.id).single();
        if (existingTemplate) oldTemplate = existingTemplate;
      }
      
      const { data, error } = await supabase.from('sequence_templates').upsert(template).select().single();
      
      // Logger l'audit
      if (data && !error) {
        const actionType = oldTemplate ? 'UPDATE' : 'INSERT';
        await auditLogger.log(actionType, 'sequence_templates', data.id, oldTemplate, template);
      }
      
      return { data, error };
  },

  deleteSequenceTemplate: async (id: string) => {
      // Récupérer le template avant suppression pour l'audit
      const { data: oldTemplate } = await supabase.from('sequence_templates').select('*').eq('id', id).single();
      
      const { error } = await supabase.from('sequence_templates').delete().eq('id', id);
      
      // Logger l'audit
      if (!error && oldTemplate) {
        await auditLogger.logDelete('sequence_templates', id, oldTemplate);
      }
      
      return { error };
  },

  // --- Marketing Sequences Templates CRUD ---
  getMarketingSequenceTemplates: async (providerId?: string): Promise<SequenceTemplate[]> => {
      let query = supabase.from('marketing_sequence_templates').select('*');
      
      // Filtrer par provider si fourni
      if (providerId) {
          query = query.eq('provider_id', providerId);
      }
      
      const { data, error } = await query;
      if (error) {
          console.error("Error fetching marketing sequence templates", error);
          return [];
      }
      
      // Mapper les données pour correspondre à SequenceTemplate (sans provider_id dans le type)
      return (data || []).map((t: any) => ({
          id: t.id,
          name: t.name,
          description: t.description || '',
          color: t.color || 'blue',
          icon: t.icon || '📧',
          steps: t.steps || []
      }));
  },

  saveMarketingSequenceTemplate: async (template: SequenceTemplate & { providerId?: string | null }) => {
      // Récupérer l'ancien template si c'est une mise à jour
      let oldTemplate: any = null;
      if (template.id) {
        const { data: existingTemplate } = await supabase.from('marketing_sequence_templates').select('*').eq('id', template.id).single();
        if (existingTemplate) oldTemplate = existingTemplate;
      }
      
      // Récupérer le provider du user courant si providerId n'est pas fourni
      let providerId = template.providerId;
      if (!providerId) {
          const session = await supabase.auth.getSession();
          const userId = session.data.session?.user?.id;
          if (userId) {
              const userProvider = await storage.getUserMarketingProvider(userId);
              if (userProvider) {
                  providerId = userProvider.id;
              }
          }
      }
      
      const dbTemplate: any = {
          id: template.id,
          provider_id: providerId,
          name: template.name,
          description: template.description,
          color: template.color,
          icon: template.icon,
          steps: template.steps
      };
      
      const { data, error } = await supabase.from('marketing_sequence_templates').upsert(dbTemplate).select().single();
      
      // Logger l'audit
      if (data && !error) {
        const actionType = oldTemplate ? 'UPDATE' : 'INSERT';
        await auditLogger.log(actionType, 'marketing_sequence_templates', data.id, oldTemplate, dbTemplate);
      }
      
      return { data, error };
  },

  deleteMarketingSequenceTemplate: async (id: string) => {
      // Récupérer le template avant suppression pour l'audit
      const { data: oldTemplate } = await supabase.from('marketing_sequence_templates').select('*').eq('id', id).single();
      
      const { error } = await supabase.from('marketing_sequence_templates').delete().eq('id', id);
      
      // Logger l'audit
      if (!error && oldTemplate) {
        await auditLogger.logDelete('marketing_sequence_templates', id, oldTemplate);
      }
      
      return { error };
  },

  // --- Document Categories CRUD ---
  getDocumentCategories: async (): Promise<DocumentCategory[]> => {
      const { data, error } = await supabase.from('document_categories').select('*').order('name');
      if (error) return [];
      return data || [];
  },

  saveDocumentCategory: async (category: DocumentCategory) => {
      // Récupérer l'ancienne catégorie si c'est une mise à jour
      let oldCategory: any = null;
      if (category.id) {
        const { data: existingCategory } = await supabase.from('document_categories').select('*').eq('id', category.id).single();
        if (existingCategory) oldCategory = existingCategory;
      }
      
      const { data, error } = await supabase.from('document_categories').upsert(category).select().single();
      
      // Logger l'audit
      if (data && !error) {
        const actionType = oldCategory ? 'UPDATE' : 'INSERT';
        await auditLogger.log(actionType, 'document_categories', data.id, oldCategory, category);
      }
      
      return { data, error };
  },

  deleteDocumentCategory: async (id: string) => {
      // Récupérer la catégorie avant suppression pour l'audit
      const { data: oldCategory } = await supabase.from('document_categories').select('*').eq('id', id).single();
      
      const { error } = await supabase.from('document_categories').delete().eq('id', id);
      
      // Logger l'audit
      if (!error && oldCategory) {
        await auditLogger.logDelete('document_categories', id, oldCategory);
      }
      
      return { error };
  },

  // --- Checklist Templates CRUD ---
  getChecklistTemplates: async (): Promise<ChecklistTemplate[]> => {
      const { data, error } = await supabase.from('checklist_templates').select('*').order('order_index');
      if (error) return [];
      return data || [];
  },

  saveChecklistTemplate: async (template: ChecklistTemplate) => {
      // Récupérer l'ancien template si c'est une mise à jour
      let oldTemplate: any = null;
      if (template.id) {
        const { data: existingTemplate } = await supabase.from('checklist_templates').select('*').eq('id', template.id).single();
        if (existingTemplate) oldTemplate = existingTemplate;
      }
      
      const { data, error } = await supabase.from('checklist_templates').upsert(template).select().single();
      
      // Logger l'audit
      if (data && !error) {
        const actionType = oldTemplate ? 'UPDATE' : 'INSERT';
        await auditLogger.log(actionType, 'checklist_templates', data.id, oldTemplate, template);
      }
      
      return { data, error };
  },

  deleteChecklistTemplate: async (id: string) => {
      // Récupérer le template avant suppression pour l'audit
      const { data: oldTemplate } = await supabase.from('checklist_templates').select('*').eq('id', id).single();
      
      const { error } = await supabase.from('checklist_templates').delete().eq('id', id);
      
      // Logger l'audit
      if (!error && oldTemplate) {
        await auditLogger.logDelete('checklist_templates', id, oldTemplate);
      }
      
      return { error };
  },

  // --- AI USAGE TRACKING ---
  // Helper function to calculate cost based on tokens (Gemini 2.5 Flash pricing)
  // Pricing: $0.075 per 1M input tokens, $0.30 per 1M output tokens
  calculateAICost: (inputTokens: number, outputTokens: number): number => {
      const inputCost = (inputTokens / 1_000_000) * 0.075;
      const outputCost = (outputTokens / 1_000_000) * 0.30;
      return inputCost + outputCost;
  },

  // Record AI usage (non-blocking - errors are logged but don't throw)
  recordAIUsage: async (usageType: AIUsageType, inputTokens: number, outputTokens: number, model: string = 'gemini-2.5-flash') => {
      try {
          const user = await storage.getUser();
          if (!user) {
              console.warn('Cannot record AI usage: No user session');
              return { error: null }; // Don't block the user flow
          }

          const totalTokens = inputTokens + outputTokens;
          const costUsd = storage.calculateAICost(inputTokens, outputTokens);

          const dbUsage = {
              user_id: user.id,
              usage_type: usageType,
              model: model,
              input_tokens: inputTokens,
              output_tokens: outputTokens,
              total_tokens: totalTokens,
              cost_usd: costUsd
          };

          const { error } = await supabase.from('ai_usage').insert(dbUsage);
          if (error) {
              console.error('Error recording AI usage (non-blocking):', error);
              // Don't throw - just log the error
          }
          return { error: null };
      } catch (e) {
          console.error('Unexpected error recording AI usage (non-blocking):', e);
          return { error: null }; // Always return success to not block user flow
      }
  },

  // Get AI usage statistics by user and date range
  getAIUsageStats: async (startDate?: string, endDate?: string): Promise<AIUsageStats[]> => {
      // First, get all AI usage records
      let query = supabase
          .from('ai_usage')
          .select('user_id, usage_type, total_tokens, cost_usd');

      if (startDate) {
          query = query.gte('created_at', startDate);
      }
      if (endDate) {
          query = query.lte('created_at', endDate);
      }

      const { data: usageData, error: usageError } = await query;
      if (usageError) {
          console.error('Error fetching AI usage stats:', usageError);
          return [];
      }

      if (!usageData || usageData.length === 0) {
          return [];
      }

      // Get unique user IDs (filter out null/undefined values)
      const userIds = [...new Set(usageData.map((row: any) => row.user_id).filter((id: any) => id != null))];
      
      // Fetch profiles for these users (only if we have valid IDs)
      let profilesData: any[] = [];
      let profilesError: any = null;
      
      if (userIds.length > 0) {
          const result = await supabase
              .from('profiles')
              .select('id, email, first_name, last_name')
              .in('id', userIds);
          profilesData = result.data || [];
          profilesError = result.error;
      }

      if (profilesError) {
          console.error('Error fetching profiles:', profilesError);
      }

      // Create a map of user ID to profile
      const profileMap = new Map<string, any>();
      profilesData?.forEach((profile: any) => {
          profileMap.set(profile.id, profile);
      });

      // Group by user
      const userMap = new Map<string, AIUsageStats>();
      const UNKNOWN_USER_ID = '__unknown__';

      usageData.forEach((row: any) => {
          const userId = row.user_id;
          
          // Use special key for null user_id to group unknown users
          const mapKey = userId || UNKNOWN_USER_ID;
          const profile = userId ? profileMap.get(userId) : null;
          
          if (!userMap.has(mapKey)) {
              userMap.set(mapKey, {
                  userId: userId || UNKNOWN_USER_ID,
                  userEmail: profile?.email || '',
                  userName: profile 
                      ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.email || 'Utilisateur inconnu'
                      : 'Utilisateur inconnu',
                  totalTokens: 0,
                  totalCostUsd: 0,
                  usageCount: 0,
                  usageByType: {
                      ESTIMATE_PROPOSAL: 0,
                      GENERATE_PROPERTY_NAME: 0,
                      PARSE_LEBONCOIN: 0,
                      ESTIMATE_REVENUE: 0
                  }
              });
          }

          const stats = userMap.get(mapKey)!;
          stats.totalTokens += row.total_tokens || 0;
          stats.totalCostUsd += parseFloat(row.cost_usd || 0);
          stats.usageCount += 1;
          stats.usageByType[row.usage_type as AIUsageType] += 1;
      });

      return Array.from(userMap.values());
  },

  // Get detailed AI usage list
  getAIUsageList: async (startDate?: string, endDate?: string, userId?: string): Promise<AIUsage[]> => {
      let query = supabase
          .from('ai_usage')
          .select('*')
          .order('created_at', { ascending: false });

      if (startDate) {
          query = query.gte('created_at', startDate);
      }
      if (endDate) {
          query = query.lte('created_at', endDate);
      }
      if (userId) {
          query = query.eq('user_id', userId);
      }

      const { data, error } = await query;
      if (error) {
          console.error('Error fetching AI usage list:', error);
          return [];
      }

      return data?.map((d: any) => ({
          id: d.id,
          userId: d.user_id,
          usageType: d.usage_type as AIUsageType,
          model: d.model,
          inputTokens: d.input_tokens,
          outputTokens: d.output_tokens,
          totalTokens: d.total_tokens,
          costUsd: parseFloat(d.cost_usd),
          createdAt: d.created_at
      })) || [];
  },


  // --- AUDIT LOGS ---
  getAuditLogs: async (filters?: {
    tableName?: string;
    actionType?: AuditActionType;
    userId?: string;
    recordId?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }): Promise<AuditLog[]> => {
    let query = supabase
      .from('audit_logs')
      .select('*, profiles:user_id(email, first_name, last_name)')
      .order('created_at', { ascending: false });

    if (filters?.tableName) {
      query = query.eq('table_name', filters.tableName);
    }
    if (filters?.actionType) {
      query = query.eq('action_type', filters.actionType);
    }
    if (filters?.userId) {
      query = query.eq('user_id', filters.userId);
    }
    if (filters?.recordId) {
      query = query.eq('record_id', filters.recordId);
    }
    if (filters?.startDate) {
      query = query.gte('created_at', filters.startDate);
    }
    if (filters?.endDate) {
      query = query.lte('created_at', filters.endDate);
    }
    if (filters?.limit) {
      query = query.limit(filters.limit);
    }
    if (filters?.offset) {
      query = query.range(filters.offset, (filters.offset + (filters.limit || 100) - 1));
    }

    const { data, error } = await query;
    if (error) {
      console.error('Error fetching audit logs:', error);
      return [];
    }

    return (data || []).map((log: any) => ({
      id: log.id,
      userId: log.user_id,
      actionType: log.action_type as AuditActionType,
      tableName: log.table_name,
      recordId: log.record_id,
      oldData: log.old_data ? (typeof log.old_data === 'string' ? JSON.parse(log.old_data) : log.old_data) : null,
      newData: log.new_data ? (typeof log.new_data === 'string' ? JSON.parse(log.new_data) : log.new_data) : null,
      createdAt: log.created_at
    }));
  },

  getAuditLogsCount: async (filters?: {
    tableName?: string;
    actionType?: AuditActionType;
    userId?: string;
    recordId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<number> => {
    let query = supabase
      .from('audit_logs')
      .select('*', { count: 'exact', head: true });

    if (filters?.tableName) {
      query = query.eq('table_name', filters.tableName);
    }
    if (filters?.actionType) {
      query = query.eq('action_type', filters.actionType);
    }
    if (filters?.userId) {
      query = query.eq('user_id', filters.userId);
    }
    if (filters?.recordId) {
      query = query.eq('record_id', filters.recordId);
    }
    if (filters?.startDate) {
      query = query.gte('created_at', filters.startDate);
    }
    if (filters?.endDate) {
      query = query.lte('created_at', filters.endDate);
    }

    const { count, error } = await query;
    if (error) {
      console.error('Error counting audit logs:', error);
      return 0;
    }
    return count || 0;
  },

  // --- SYSTEM CONFIG ---
  /**
   * Récupère la valeur d'une configuration système
   */
  getSystemConfig: async (key: string): Promise<any | null> => {
    try {
      const { data, error } = await supabase
        .from('system_config')
        .select('value')
        .eq('key', key)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null; // Not found
        console.error('Error fetching system config:', error);
        return null;
      }

      return data?.value || null;
    } catch (e: any) {
      console.error('Critical error fetching system config:', e);
      return null;
    }
  },

  /**
   * Met à jour une configuration système (superadmin seulement)
   */
  updateSystemConfig: async (key: string, value: any): Promise<{ error?: any }> => {
    try {
      const { error } = await supabase
        .from('system_config')
        .upsert({
          key,
          value,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'key'
        });

      if (error) {
        console.error('Error updating system config:', error);
        return { error };
      }

      return {};
    } catch (e: any) {
      console.error('Critical error updating system config:', e);
      return { error: e.message || 'Erreur inconnue' };
    }
  },

  /**
   * Vérifie si le mode maintenance est activé
   * Fonction publique qui peut être appelée même sans session
   */
  isMaintenanceModeEnabled: async (): Promise<boolean> => {
    try {
      // Utiliser maybeSingle() au lieu de single() pour éviter l'erreur 406 si pas de résultat
      const { data, error } = await supabase
        .from('system_config')
        .select('value')
        .eq('key', 'maintenance_mode')
        .maybeSingle();

      if (error) {
        // Si erreur d'authentification, on considère que le mode maintenance n'est pas activé
        if (error.code === 'PGRST301' || error.code === '42501') {
          return false;
        }
        console.error('Error checking maintenance mode:', error);
        return false;
      }

      if (!data) return false;
      return data.value?.enabled === true;
    } catch (e: any) {
      console.error('Error checking maintenance mode:', e);
      return false; // En cas d'erreur, on laisse l'app fonctionner
    }
  },

  /**
   * Vérifie si la gestion des finances est activée
   */
  isFinanceEnabled: async (): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('system_config')
        .select('value')
        .eq('key', 'finance_enabled')
        .maybeSingle();

      if (error) {
        console.error('Error checking finance feature flag:', error);
        return false;
      }

      if (!data) return false;
      return data.value?.enabled === true;
    } catch (e: any) {
      console.error('Error checking finance feature flag:', e);
      return false;
    }
  },

  /**
   * Configuration du module Facturation propriétaires (CRG) :
   * activé/désactivé, mode bêta et agences autorisées en bêta.
   */
  getBillingConfig: async (): Promise<BillingConfig> => {
    const fallback: BillingConfig = { enabled: false, beta: false, betaAgencyIds: [] };
    try {
      const { data, error } = await supabase
        .from('system_config')
        .select('value')
        .eq('key', 'billing_enabled')
        .maybeSingle();

      if (error) {
        console.error('Error checking billing feature flag:', error);
        return fallback;
      }

      if (!data) return fallback;
      return {
        enabled: data.value?.enabled === true,
        beta: data.value?.beta === true,
        betaAgencyIds: Array.isArray(data.value?.betaAgencyIds) ? data.value.betaAgencyIds : [],
      };
    } catch (e: any) {
      console.error('Error checking billing feature flag:', e);
      return fallback;
    }
  },

  /**
   * Récupère le message de maintenance
   * Fonction publique qui peut être appelée même sans session
   */
  getMaintenanceMessage: async (): Promise<string> => {
    try {
      // Utiliser maybeSingle() au lieu de single() pour éviter l'erreur 406 si pas de résultat
      const { data, error } = await supabase
        .from('system_config')
        .select('value')
        .eq('key', 'maintenance_mode')
        .maybeSingle();

      if (error) {
        // Si erreur d'authentification, retourner le message par défaut
        if (error.code === 'PGRST301' || error.code === '42501') {
          return "L'application est actuellement en maintenance. Veuillez réessayer plus tard.";
        }
        console.error('Error fetching maintenance message:', error);
        return "L'application est actuellement en maintenance. Veuillez réessayer plus tard.";
      }

      if (!data) return "L'application est actuellement en maintenance. Veuillez réessayer plus tard.";
      return data.value?.message || "L'application est actuellement en maintenance. Veuillez réessayer plus tard.";
    } catch (e: any) {
      console.error('Error fetching maintenance message:', e);
      return "L'application est actuellement en maintenance. Veuillez réessayer plus tard.";
    }
  },

  // --- EXPENSES CRUD ---
  getExpenses: async (filters?: ExpenseFilters): Promise<Expense[]> => {
    // Vérifier si la gestion des finances est activée
    const financeEnabled = await storage.isFinanceEnabled();
    if (!financeEnabled) {
      return [];
    }

    // Si on filtre par mois/année, on doit récupérer :
    // 1. Les dépenses spécifiques du mois (year = X, month = Y)
    // 2. Les dépenses récurrentes (month IS NULL, year IS NULL, is_recurring = true)
    // 3. Exclure les récurrentes qui ont une surcharge pour ce mois
    
    let allExpenses: Expense[] = [];
    
    if (filters?.year !== undefined && filters?.month !== undefined) {
      // Requête 1: Dépenses du mois spécifique
      let monthlyQuery = supabase.from('expenses').select('*');
      if (filters?.propertyId) {
        monthlyQuery = monthlyQuery.eq('property_id', filters.propertyId);
      }
      if (filters?.agencyId) {
        monthlyQuery = monthlyQuery.eq('agency_id', filters.agencyId);
      }
      monthlyQuery = monthlyQuery.eq('year', filters.year);
      monthlyQuery = monthlyQuery.eq('month', filters.month);
      if (filters?.expenseType) {
        monthlyQuery = monthlyQuery.eq('expense_type', filters.expenseType);
      }
      if (filters?.status) {
        monthlyQuery = monthlyQuery.eq('status', filters.status);
      }
      
      const { data: monthlyData, error: monthlyError } = await monthlyQuery;
      if (monthlyError) {
        console.error('Error fetching monthly expenses:', monthlyError);
      } else {
        allExpenses.push(...(monthlyData || []));
      }
      
      // Identifier les récurrentes qui ont une surcharge pour ce mois (avant de récupérer les récurrentes)
      const monthlyExpensesData = monthlyData || [];
      const overridesForMonth = monthlyExpensesData.filter((e: any) => e.recurring_expense_id);
      const recurringIdsWithOverrides = new Set(overridesForMonth.map((e: any) => e.recurring_expense_id).filter(Boolean));
      
      // Requête 2: Dépenses récurrentes (month IS NULL, year IS NULL)
      let recurringQuery = supabase.from('expenses').select('*');
      if (filters?.propertyId) {
        recurringQuery = recurringQuery.eq('property_id', filters.propertyId);
      }
      if (filters?.agencyId) {
        recurringQuery = recurringQuery.eq('agency_id', filters.agencyId);
      }
      recurringQuery = recurringQuery.is('year', null);
      recurringQuery = recurringQuery.is('month', null);
      recurringQuery = recurringQuery.eq('is_recurring', true);
      if (filters?.expenseType) {
        recurringQuery = recurringQuery.eq('expense_type', filters.expenseType);
      }
      if (filters?.status) {
        recurringQuery = recurringQuery.eq('status', filters.status);
      }
      
      const { data: recurringData, error: recurringError } = await recurringQuery;
      if (recurringError) {
        console.error('Error fetching recurring expenses:', recurringError);
      } else {
        // Ne garder que les récurrentes qui n'ont pas de surcharge pour ce mois
        const recurringToInclude = (recurringData || []).filter((e: any) => !recurringIdsWithOverrides.has(e.id));
        allExpenses.push(...recurringToInclude);
      }
    } else {
      // Pas de filtre par mois : récupérer toutes les dépenses
      let query = supabase.from('expenses').select('*');
      
      if (filters?.propertyId) {
        query = query.eq('property_id', filters.propertyId);
      }
      if (filters?.agencyId) {
        query = query.eq('agency_id', filters.agencyId);
      }
      if (filters?.year !== undefined) {
        query = query.eq('year', filters.year);
      }
      if (filters?.month !== undefined) {
        query = query.eq('month', filters.month);
      }
      if (filters?.expenseType) {
        query = query.eq('expense_type', filters.expenseType);
      }
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      
      query = query.order('year', { ascending: false, nullsFirst: false })
                   .order('month', { ascending: false, nullsFirst: false })
                   .order('created_at', { ascending: false });
      
      if (filters?.limit) {
        query = query.limit(filters.limit);
      }
      if (filters?.offset) {
        query = query.range(filters.offset, filters.offset + (filters.limit || 100) - 1);
      }
      
      const { data, error } = await query;
      if (error) {
        console.error('Error fetching expenses:', error);
        return [];
      }
      allExpenses = data || [];
    }
    
    // Mapper les données vers le format Expense
    // Pour les dépenses récurrentes filtrées par mois, ajouter month et year pour l'affichage
    return allExpenses.map((d: any) => ({
      id: d.id,
      agencyId: d.agency_id,
      propertyId: d.property_id,
      expenseType: d.expense_type as ExpenseType,
      category: d.category,
      description: d.description,
      amount: parseFloat(d.amount || 0),
      currency: d.currency || 'EUR',
      // Pour les dépenses récurrentes (month IS NULL), utiliser les valeurs du filtre pour l'affichage
      year: d.year ?? (filters?.year !== undefined ? filters.year : null),
      month: d.month ?? (filters?.month !== undefined ? filters.month : null),
      dueDate: d.due_date,
      propertyAssetReference: d.property_asset_reference,
      isSoftwareExpense: d.is_software_expense || false,
      allocatedProperties: d.allocated_properties || [],
      allocationMethod: d.allocation_method,
      isPlacidCommission: d.is_placid_commission || false,
      commissionPercentage: d.commission_percentage ? parseFloat(d.commission_percentage) : undefined,
      supplierName: d.supplier_name,
      supplierContact: d.supplier_contact,
      documentId: d.document_id,
      status: d.status as ExpenseStatus,
      isRecurring: d.is_recurring || false,
      recurringExpenseId: d.recurring_expense_id || null,
      createdAt: d.created_at,
      updatedAt: d.updated_at,
      createdBy: d.created_by
    }));
  },

  saveExpense: async (expense: Expense): Promise<{ data: Expense | null; error: any }> => {
    // Vérifier si la gestion des finances est activée
    const financeEnabled = await storage.isFinanceEnabled();
    if (!financeEnabled) {
      return { data: null, error: { message: 'La gestion des finances est désactivée' } };
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: null, error: { message: 'Not authenticated' } };

    // Récupérer l'ancienne expense si c'est une mise à jour
    let oldExpense: any = null;
    if (expense.id) {
      const { data: existingExpense } = await supabase.from('expenses').select('*').eq('id', expense.id).single();
      if (existingExpense) oldExpense = existingExpense;
    }

    const dbExpense: any = {
      id: expense.id,
      agency_id: expense.agencyId,
      property_id: expense.propertyId || null,
      expense_type: expense.expenseType,
      category: expense.category,
      description: expense.description,
      amount: expense.amount,
      currency: expense.currency || 'EUR',
      year: expense.year ?? null, // NULL pour dépense récurrente
      month: expense.month ?? null, // NULL pour dépense récurrente
      due_date: expense.dueDate,
      property_asset_reference: expense.propertyAssetReference,
      is_software_expense: expense.isSoftwareExpense || false,
      allocated_properties: expense.allocatedProperties || [],
      allocation_method: expense.allocationMethod,
      is_placid_commission: expense.isPlacidCommission || false,
      commission_percentage: expense.commissionPercentage,
      supplier_name: expense.supplierName,
      supplier_contact: expense.supplierContact,
      document_id: expense.documentId,
      status: expense.status || 'PENDING',
      is_recurring: expense.isRecurring || false,
      recurring_expense_id: expense.recurringExpenseId || null, // Pour les surcharges mensuelles
      created_by: user.id
    };
    
    // Si c'est une dépense récurrente, forcer month et year à NULL
    if (expense.isRecurring) {
      dbExpense.year = null;
      dbExpense.month = null;
    }

    Object.keys(dbExpense).forEach(key => dbExpense[key] === undefined && delete dbExpense[key]);
    // Ne pas générer d'ID ici - la base de données génère automatiquement avec DEFAULT gen_random_uuid()
    // On laisse Supabase gérer la génération d'UUID

    const { data, error } = await supabase.from('expenses').upsert(dbExpense).select().single();

    // Logger l'audit
    if (data && !error) {
      const actionType = oldExpense ? 'UPDATE' : 'INSERT';
      await auditLogger.log(actionType, 'expenses', data.id, oldExpense, dbExpense);
    }

    if (data) {
      return {
        data: {
          ...expense,
          id: data.id,
          createdAt: data.created_at,
          updatedAt: data.updated_at,
          createdBy: data.created_by
        },
        error: null
      };
    }
    return { data: null, error };
  },

  deleteExpense: async (id: string): Promise<{ error: any }> => {
    // Vérifier si la gestion des finances est activée
    const financeEnabled = await storage.isFinanceEnabled();
    if (!financeEnabled) {
      return { error: { message: 'La gestion des finances est désactivée' } };
    }

    // Récupérer l'expense avant suppression pour l'audit
    const { data: oldExpense } = await supabase.from('expenses').select('*').eq('id', id).single();

    const { error } = await supabase.from('expenses').delete().eq('id', id);

    // Logger l'audit
    if (!error && oldExpense) {
      await auditLogger.logDelete('expenses', id, oldExpense);
    }

    return { error };
  },

  // --- REVENUE CRUD ---
  getRevenue: async (propertyId?: string, year?: number, month?: number): Promise<Revenue[]> => {
    let query = supabase.from('revenue').select('*');
    
    if (propertyId) {
      query = query.eq('property_id', propertyId);
    }
    if (year !== undefined) {
      query = query.eq('year', year);
    }
    if (month !== undefined) {
      query = query.eq('month', month);
    }
    
    query = query.order('year', { ascending: false })
                 .order('month', { ascending: false })
                 .order('created_at', { ascending: false });
    
    const { data, error } = await query;
    if (error) {
      console.error('Error fetching revenue:', error);
      return [];
    }
    
    return data?.map((d: any) => ({
      id: d.id,
      agencyId: d.agency_id,
      propertyId: d.property_id,
      year: d.year,
      month: d.month,
      nightlyRatesTotal: parseFloat(d.nightly_rates_total || 0),
      taxesCollected: parseFloat(d.taxes_collected || 0),
      cleaningFeesCollected: parseFloat(d.cleaning_fees_collected || 0),
      airbnbCommissionRate: parseFloat(d.airbnb_commission_rate || 15.5),
      airbnbCommissionAmount: parseFloat(d.airbnb_commission_amount || 0),
      bookingCommissionRate: parseFloat(d.booking_commission_rate || 18.0),
      bookingCommissionAmount: parseFloat(d.booking_commission_amount || 0),
      otherPlatformCommissions: d.other_platform_commissions || {},
      grossRevenue: parseFloat(d.gross_revenue || 0),
      totalCommissions: parseFloat(d.total_commissions || 0),
      netRevenue: parseFloat(d.net_revenue || 0),
      source: d.source || 'MANUAL',
      notes: d.notes,
      createdAt: d.created_at,
      updatedAt: d.updated_at,
      createdBy: d.created_by
    })) || [];
  },

  saveRevenue: async (revenue: Revenue): Promise<{ data: Revenue | null; error: any }> => {
    // Vérifier si la gestion des finances est activée
    const financeEnabled = await storage.isFinanceEnabled();
    if (!financeEnabled) {
      return { data: null, error: { message: 'La gestion des finances est désactivée' } };
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: null, error: { message: 'Not authenticated' } };

    // Récupérer l'ancien revenue si c'est une mise à jour
    let oldRevenue: any = null;
    if (revenue.id) {
      const { data: existingRevenue } = await supabase.from('revenue').select('*').eq('id', revenue.id).single();
      if (existingRevenue) oldRevenue = existingRevenue;
    }

    const dbRevenue: any = {
      id: revenue.id,
      agency_id: revenue.agencyId,
      property_id: revenue.propertyId,
      year: revenue.year,
      month: revenue.month,
      nightly_rates_total: revenue.nightlyRatesTotal,
      taxes_collected: revenue.taxesCollected,
      cleaning_fees_collected: revenue.cleaningFeesCollected,
      airbnb_commission_rate: revenue.airbnbCommissionRate,
      booking_commission_rate: revenue.bookingCommissionRate,
      other_platform_commissions: revenue.otherPlatformCommissions || {},
      source: revenue.source || 'MANUAL',
      notes: revenue.notes,
      created_by: user.id
    };

    Object.keys(dbRevenue).forEach(key => dbRevenue[key] === undefined && delete dbRevenue[key]);
    // Ne pas générer d'ID ici - la base de données génère automatiquement avec DEFAULT gen_random_uuid()
    // On laisse Supabase gérer la génération d'UUID

    // Le trigger calcule automatiquement les totaux (gross_revenue, commissions, net_revenue)
    const { data, error } = await supabase.from('revenue').upsert(dbRevenue).select().single();

    // Logger l'audit
    if (data && !error) {
      const actionType = oldRevenue ? 'UPDATE' : 'INSERT';
      await auditLogger.log(actionType, 'revenue', data.id, oldRevenue, dbRevenue);

      // Après sauvegarde, recalculer la commission Placid du mois
      await storage.calculatePlacidCommission(revenue.propertyId, revenue.year, revenue.month);
    }

    if (data) {
      return {
        data: {
          ...revenue,
          id: data.id,
          grossRevenue: parseFloat(data.gross_revenue || 0),
          airbnbCommissionAmount: parseFloat(data.airbnb_commission_amount || 0),
          bookingCommissionAmount: parseFloat(data.booking_commission_amount || 0),
          totalCommissions: parseFloat(data.total_commissions || 0),
          netRevenue: parseFloat(data.net_revenue || 0),
          createdAt: data.created_at,
          updatedAt: data.updated_at,
          createdBy: data.created_by
        },
        error: null
      };
    }
    return { data: null, error };
  },

  // --- PLACID COMMISSION CALCULATION ---
  calculatePlacidCommission: async (propertyId: string, year: number, month: number): Promise<number> => {
    // 1. Récupérer les revenus du mois
    const revenues = await storage.getRevenue(propertyId, year, month);
    if (!revenues || revenues.length === 0 || !revenues[0].netRevenue) {
      return 0;
    }
    
    const revenue = revenues[0];
    const netRevenue = revenue.netRevenue;
    
    // 2. Récupérer l'agence et son % commission
    const properties = await storage.getProperties();
    const property = properties.find(p => p.id === propertyId);
    if (!property || !property.agencyId) {
      return 0;
    }
    
    const agencies = await storage.getAllAgencies();
    const agency = agencies.find(a => a.id === property.agencyId);
    if (!agency || !agency.placidCommissionRate || agency.placidCommissionRate === 0) {
      return 0;
    }
    
    const commissionRate = agency.placidCommissionRate;
    
    // 3. Calculer la commission
    const commissionAmount = netRevenue * (commissionRate / 100);
    
    // 4. Créer/mettre à jour la dépense
    const existingExpenses = await storage.getExpenses({
      propertyId,
      year,
      month,
      expenseType: 'PLACID_COMMISSION'
    });
    
    const existingExpense = existingExpenses[0];
    
    if (existingExpense) {
      await storage.saveExpense({
        ...existingExpense,
        amount: commissionAmount,
        commissionPercentage: commissionRate,
        status: 'PENDING'
      });
    } else {
      await storage.saveExpense({
        propertyId,
        agencyId: property.agencyId,
        expenseType: 'PLACID_COMMISSION',
        category: 'Cotisation Placid',
        amount: commissionAmount,
        year,
        month,
        isPlacidCommission: true,
        commissionPercentage: commissionRate,
        status: 'PENDING',
        currency: 'EUR'
      });
    }
    
    return commissionAmount;
  },

  // --- ROI CALCULATION ---
  calculatePropertyROI: async (propertyId: string, year: number, month: number): Promise<PropertyROI | null> => {
    // 1. Récupérer les revenus du mois
    const revenues = await storage.getRevenue(propertyId, year, month);
    const revenue = revenues[0];
    const totalRevenue = revenue?.netRevenue || 0;
    
    // 2. Récupérer toutes les dépenses du mois (y compris commission Placid)
    const expenses = await storage.getExpenses({ propertyId, year, month });
    
    // 3. Calculer total dépenses et par catégorie
    const totalExpenses = expenses.reduce((sum, exp) => sum + exp.amount, 0);
    const expensesByCategory: Record<string, number> = {};
    
    expenses.forEach(exp => {
      if (!expensesByCategory[exp.category]) {
        expensesByCategory[exp.category] = 0;
      }
      expensesByCategory[exp.category] += exp.amount;
    });
    
    // 4. Commission Placid séparée
    const placidCommissionExpense = expenses.find(e => e.isPlacidCommission);
    const totalPlacidCommission = placidCommissionExpense?.amount || 0;
    
    // 5. Calculer net profit
    const netProfit = totalRevenue - totalExpenses - totalPlacidCommission;
    
    // 6. Calculer ROI et margin
    const roiPercentage = totalExpenses > 0 ? (netProfit / totalExpenses) * 100 : 0;
    const margin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;
    
    // 7. Récupérer le nom du bien
    const properties = await storage.getProperties();
    const property = properties.find(p => p.id === propertyId);
    const propertyName = property?.name || property?.address || 'Bien inconnu';
    
    return {
      propertyId,
      propertyName,
      year,
      month,
      totalRevenue,
      totalPlacidCommission,
      totalExpenses,
      expensesByCategory,
      netProfit,
      roiPercentage,
      margin
    };
  },

  getMonthlyFinancialSummary: async (agencyId?: string, year?: number, month?: number): Promise<MonthlyFinancialSummary | null> => {
    if (!year || !month) return null;
    
    const currentYear = year || new Date().getFullYear();
    const currentMonth = month || new Date().getMonth() + 1;
    
    // Récupérer tous les revenus du mois
    const allRevenue = await storage.getRevenue(undefined, currentYear, currentMonth);
    const filteredRevenue = agencyId 
      ? allRevenue.filter(r => r.agencyId === agencyId)
      : allRevenue;
    
    // Récupérer toutes les dépenses du mois
    const allExpenses = await storage.getExpenses({ 
      agencyId, 
      year: currentYear, 
      month: currentMonth 
    });
    
    // Calculer totaux
    const totalRevenue = filteredRevenue.reduce((sum, r) => sum + r.netRevenue, 0);
    const totalExpenses = allExpenses
      .filter(e => !e.isPlacidCommission)
      .reduce((sum, e) => sum + e.amount, 0);
    const totalPlacidCommission = allExpenses
      .filter(e => e.isPlacidCommission)
      .reduce((sum, e) => sum + e.amount, 0);
    const netProfit = totalRevenue - totalExpenses - totalPlacidCommission;
    
    // Par bien
    const properties = await storage.getProperties();
    const byProperty = filteredRevenue.map(rev => {
      const propertyExpenses = allExpenses
        .filter(e => e.propertyId === rev.propertyId && !e.isPlacidCommission);
      const propertyExpensesTotal = propertyExpenses.reduce((sum, e) => sum + e.amount, 0);
      const propertyPlacidCommission = allExpenses
        .filter(e => e.propertyId === rev.propertyId && e.isPlacidCommission)
        .reduce((sum, e) => sum + e.amount, 0);
      const propertyNetProfit = rev.netRevenue - propertyExpensesTotal - propertyPlacidCommission;
      const propertyROI = propertyExpensesTotal > 0 
        ? (propertyNetProfit / propertyExpensesTotal) * 100 
        : 0;
      
      const property = properties.find(p => p.id === rev.propertyId);
      const propertyName = property?.name || property?.address || 'Bien inconnu';
      
      return {
        propertyId: rev.propertyId,
        propertyName,
        revenue: rev.netRevenue,
        expenses: propertyExpensesTotal,
        placidCommission: propertyPlacidCommission,
        netProfit: propertyNetProfit,
        roi: propertyROI
      };
    });
    
    return {
      year: currentYear,
      month: currentMonth,
      agencyId,
      totalRevenue,
      totalExpenses,
      totalPlacidCommission,
      netProfit,
      byProperty
    };
  },

  // Synchroniser les assets d'une propriété vers les dépenses récurrentes
  syncAssetsToExpenses: async (propertyId: string): Promise<void> => {
    // Récupérer la propriété avec ses assets
    const properties = await storage.getProperties();
    const property = properties.find(p => p.id === propertyId);
    
    if (!property || !property.assets) return;
    
    // Mapper AssetType vers ExpenseType
    const assetToExpenseType: Record<string, ExpenseType> = {
      'INTERNET': 'UTILITIES',
      'ELECTRICITY': 'UTILITIES',
      'WATER': 'UTILITIES',
      'GAS': 'UTILITIES',
      'INSURANCE': 'INSURANCE',
      'OTHER': 'OTHER'
    };
    
    // Mapper AssetType vers catégorie
    const assetToCategory: Record<string, string> = {
      'INTERNET': 'Internet',
      'ELECTRICITY': 'Électricité',
      'WATER': 'Eau',
      'GAS': 'Gaz',
      'INSURANCE': 'Assurance Habitation',
      'OTHER': 'Autre'
    };
    
    // Pour chaque asset actif avec un coût mensuel
    for (const asset of property.assets) {
      if (asset.status === 'ACTIVE' && asset.monthlyCost && asset.monthlyCost > 0) {
        // Récupérer toutes les dépenses récurrentes pour cette propriété
        // On les récupère pour un mois arbitraire (les récurrentes sont incluses)
        const allExpensesForProperty = await storage.getExpenses({
          propertyId,
          year: new Date().getFullYear(),
          month: 1
        });
        
        // Filtrer les dépenses récurrentes liées à cet asset
        const existingExpense = allExpensesForProperty.find(
          e => e.propertyAssetReference === asset.id && 
               e.isRecurring && 
               (e.month === null || e.month === undefined) && 
               (e.year === null || e.year === undefined)
        );
        
        if (existingExpense) {
          // Mettre à jour le montant et les informations si nécessaire
          if (existingExpense.amount !== asset.monthlyCost ||
              existingExpense.supplierName !== asset.provider ||
              existingExpense.supplierContact !== asset.contractNumber) {
            await storage.saveExpense({
              ...existingExpense,
              amount: asset.monthlyCost,
              category: assetToCategory[asset.type] || 'Autre',
              supplierName: asset.provider,
              supplierContact: asset.contractNumber,
              description: `Abonnement ${asset.provider} - Contrat ${asset.contractNumber}`
            });
          }
        } else {
          // Créer une nouvelle dépense récurrente
          await storage.saveExpense({
            propertyId,
            agencyId: property.agencyId || null,
            expenseType: assetToExpenseType[asset.type] || 'OTHER',
            category: assetToCategory[asset.type] || 'Autre',
            amount: asset.monthlyCost,
            currency: 'EUR',
            year: null,
            month: null,
            isRecurring: true,
            propertyAssetReference: asset.id,
            supplierName: asset.provider,
            supplierContact: asset.contractNumber,
            description: `Abonnement ${asset.provider} - Contrat ${asset.contractNumber}`
          } as Expense);
        }
      }
    }
    
    // Supprimer les dépenses récurrentes liées à des assets qui n'existent plus ou sont inactifs
    const assetIds = property.assets
      .filter(a => a.status === 'ACTIVE' && a.monthlyCost && a.monthlyCost > 0)
      .map(a => a.id);
    
    // Récupérer toutes les dépenses récurrentes pour cette propriété
    const allRecurringExpenses = await storage.getExpenses({
      propertyId,
      year: new Date().getFullYear(),
      month: 1
    });
    
    const linkedRecurringExpenses = allRecurringExpenses.filter(
      e => e.propertyAssetReference && 
           e.isRecurring && 
           (e.month === null || e.month === undefined) && 
           (e.year === null || e.year === undefined)
    );
    
    for (const expense of linkedRecurringExpenses) {
      if (expense.propertyAssetReference && !assetIds.includes(expense.propertyAssetReference)) {
        // L'asset n'existe plus ou est inactif, supprimer la dépense récurrente
        if (expense.id) {
          await storage.deleteExpense(expense.id);
        }
      }
    }
  },

  // --- MARKETING PROVIDERS ---
  getMarketingProviders: async (): Promise<MarketingProvider[]> => {
    const { data, error } = await supabase.from('marketing_providers').select('*').order('name', { ascending: true });
    if (error) {
      console.error('Error fetching marketing providers', error);
      return [];
    }
    return data?.map((d: any) => ({
      id: d.id,
      name: d.name,
      email: d.email,
      createdAt: d.created_at,
      updatedAt: d.updated_at
    })) || [];
  },

  getMarketingProvider: async (providerId: string): Promise<MarketingProvider | null> => {
    const { data, error } = await supabase.from('marketing_providers').select('*').eq('id', providerId).single();
    if (error) {
      console.error('Error fetching marketing provider', error);
      return null;
    }
    if (!data) return null;
    return {
      id: data.id,
      name: data.name,
      email: data.email,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
  },

  getUserMarketingProvider: async (userId: string): Promise<MarketingProvider | null> => {
    const { data, error } = await supabase
      .from('marketing_provider_users')
      .select('provider_id, marketing_providers(*)')
      .eq('user_id', userId)
      .single();
    if (error || !data) {
      return null;
    }
    const provider = data.marketing_providers as any;
    if (!provider) return null;
    return {
      id: provider.id,
      name: provider.name,
      email: provider.email,
      createdAt: provider.created_at,
      updatedAt: provider.updated_at
    };
  },

  saveMarketingProvider: async (provider: Partial<MarketingProvider>) => {
    const dbProvider: any = {
      id: provider.id,
      name: provider.name,
      email: provider.email
    };

    // Récupérer l'ancien provider si c'est une mise à jour
    let oldProvider: any = null;
    if (dbProvider.id) {
      const { data: existing } = await supabase.from('marketing_providers').select('*').eq('id', dbProvider.id).single();
      if (existing) oldProvider = existing;
    }

    if (!dbProvider.id) delete (dbProvider as any).id;

    const { data, error } = await supabase.from('marketing_providers').upsert(dbProvider).select().single();
    if (error) console.error('Error saving marketing provider:', error);

    // Logger l'audit
    if (data && !error) {
      const actionType = oldProvider ? 'UPDATE' : 'INSERT';
      await auditLogger.log(actionType, 'marketing_providers', data.id, oldProvider, dbProvider);
    }

    if (data) {
      return {
        data: {
          id: data.id,
          name: data.name,
          email: data.email,
          createdAt: data.created_at,
          updatedAt: data.updated_at
        },
        error: null
      };
    }
    return { data: null, error };
  },

  linkUserToMarketingProvider: async (providerId: string, userId: string, isSupervisor: boolean, agencyIds?: string[]) => {
    // Récupérer l'ancien lien si existe
    const { data: oldLink } = await supabase
      .from('marketing_provider_users')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle(); // Utiliser maybeSingle() pour éviter l'erreur si aucun lien n'existe

    // Supprimer l'ancien lien si existe
    if (oldLink) {
      await supabase.from('marketing_provider_users').delete().eq('user_id', userId);
      // Supprimer les agences liées (la table utilise user_id directement)
      await supabase.from('marketing_provider_user_agencies').delete().eq('user_id', userId);
    }

    // Créer le nouveau lien
    const { data: linkData, error: linkError } = await supabase
      .from('marketing_provider_users')
      .insert({
        provider_id: providerId,
        user_id: userId,
        is_supervisor: isSupervisor
      })
      .select()
      .single();

    if (linkError) {
      console.error('Error linking user to marketing provider', linkError);
      return { error: linkError };
    }

    // Si setter, ajouter les agences (la table utilise user_id directement)
    if (!isSupervisor && agencyIds && agencyIds.length > 0) {
      const agenciesToInsert = agencyIds.map(agencyId => ({
        user_id: userId,
        agency_id: agencyId
      }));

      const { error: agenciesError } = await supabase
        .from('marketing_provider_user_agencies')
        .insert(agenciesToInsert);

      if (agenciesError) {
        console.error('Error linking user agencies', agenciesError);
        return { error: agenciesError };
      }
    }

    // Audit log
    if (linkData) {
      await auditLogger.log(oldLink ? 'UPDATE' : 'INSERT', 'marketing_provider_users', linkData.id, oldLink, linkData);
    }

    return { data: linkData, error: null };
  },

  updateMarketingProviderUser: async (userId: string, isSupervisor: boolean, agencyIds?: string[]) => {
    // Récupérer le provider du user
    const { data: userLink, error: fetchError } = await supabase
      .from('marketing_provider_users')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle(); // Utiliser maybeSingle() pour éviter l'erreur si aucun lien n'existe

    if (fetchError && fetchError.code !== 'PGRST116') {
      console.error('Error fetching marketing provider user:', fetchError);
      return { error: fetchError };
    }

    if (!userLink) {
      return { error: new Error('User not linked to a marketing provider') };
    }

    const oldLink = { ...userLink };

    // Mettre à jour le lien
    const { data: updatedLink, error: updateError } = await supabase
      .from('marketing_provider_users')
      .update({ is_supervisor: isSupervisor })
      .eq('user_id', userId)
      .select()
      .single();

    if (updateError) {
      console.error('Error updating marketing provider user', updateError);
      return { error: updateError };
    }

    // Supprimer les anciennes agences
    await supabase.from('marketing_provider_user_agencies').delete().eq('user_id', userId);

    // Si setter, ajouter les nouvelles agences
    if (!isSupervisor && agencyIds && agencyIds.length > 0) {
      const agenciesToInsert = agencyIds.map(agencyId => ({
        user_id: userId,
        agency_id: agencyId
      }));

      const { error: agenciesError } = await supabase
        .from('marketing_provider_user_agencies')
        .insert(agenciesToInsert);

      if (agenciesError) {
        console.error('Error updating user agencies', agenciesError);
        return { error: agenciesError };
      }
    }

    // Audit log
    if (updatedLink) {
      await auditLogger.logUpdate('marketing_provider_users', updatedLink.id, oldLink, updatedLink);
    }

    return { data: updatedLink, error: null };
  },

  getMarketingProviderUserAgencies: async (userId: string): Promise<string[]> => {
    const { data, error } = await supabase
      .from('marketing_provider_user_agencies')
      .select('agency_id')
      .eq('user_id', userId);
    if (error) {
      console.error('Error fetching user agencies', error);
      return [];
    }
    return data?.map((d: any) => d.agency_id) || [];
  },

  getMarketingProviderUsers: async (providerId: string): Promise<Array<{ userId: string; isSupervisor: boolean }>> => {
    const { data, error } = await supabase
      .from('marketing_provider_users')
      .select('user_id, is_supervisor')
      .eq('provider_id', providerId);
    if (error) {
      console.error('Error fetching marketing provider users', error);
      return [];
    }
    return data?.map((d: any) => ({
      userId: d.user_id,
      isSupervisor: d.is_supervisor
    })) || [];
  },

  // Charger les profils des setters d'un provider (pour contourner les RLS)
  getMarketingProviderSetters: async (providerId: string): Promise<UserProfile[]> => {
    // D'abord, obtenir les IDs des utilisateurs du provider qui ne sont pas superviseurs
    const { data: providerUsers, error: providerError } = await supabase
      .from('marketing_provider_users')
      .select('user_id')
      .eq('provider_id', providerId)
      .eq('is_supervisor', false);
    
    if (providerError) {
      console.error('Error fetching provider users', providerError);
      return [];
    }
    
    if (!providerUsers || providerUsers.length === 0) {
      return [];
    }
    
    const userIds = providerUsers.map((u: any) => u.user_id);
    
    // Ensuite, charger les profils de ces utilisateurs
    const { data: profilesData, error: profilesError } = await supabase
      .from('profiles')
      .select('*')
      .in('id', userIds)
      .eq('role', 'prestataire_marketing');
    
    if (profilesError) {
      console.error('Error fetching profiles', profilesError);
      return [];
    }
    
    if (!profilesData) return [];
    
    // Enrichir avec les données marketing
    const enrichedProfiles = await Promise.all(
      profilesData.map(async (d: any) => {
        const profile: UserProfile = {
          id: d.id,
          email: d.email,
          role: d.role,
          accessLevel: d.access_level,
          firstName: d.first_name,
          lastName: d.last_name
        };
        
        const { data: providerUser } = await supabase
          .from('marketing_provider_users')
          .select('is_supervisor, provider_id')
          .eq('user_id', d.id)
          .maybeSingle();
        
        if (providerUser) {
          profile.isSupervisor = providerUser.is_supervisor || false;
          profile.marketingProviderId = providerUser.provider_id || null;
          
          if (!profile.isSupervisor) {
            const { data: userAgencies } = await supabase
              .from('marketing_provider_user_agencies')
              .select('agency_id')
              .eq('user_id', d.id);
            
            profile.marketingAgencyIds = userAgencies?.map((a: any) => a.agency_id) || [];
          } else {
            profile.marketingAgencyIds = [];
          }
        }
        
        return profile;
      })
    );
    
    return enrichedProfiles;
  },

  // --- MARKETING LEADS ---
  getMarketingLeads: async (filters?: MarketingLeadFilters): Promise<MarketingLead[]> => {
    let query = supabase.from('marketing_leads').select('*');

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }
    if (filters?.source) {
      query = query.eq('source', filters.source);
    }
    if (filters?.providerId) {
      query = query.eq('provider_id', filters.providerId);
    }
    if (filters?.campaignId) {
      query = query.eq('campaign_id', filters.campaignId);
    }
    if (filters?.agencyId) {
      query = query.eq('agency_id', filters.agencyId);
    }
    if (filters?.startDate) {
      query = query.gte('received_at', filters.startDate);
    }
    if (filters?.endDate) {
      query = query.lte('received_at', filters.endDate);
    }
    if (filters?.search) {
      query = query.or(`first_name.ilike.%${filters.search}%,last_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%,phone.ilike.%${filters.search}%,property_address.ilike.%${filters.search}%,campaign_name.ilike.%${filters.search}%`);
    }

    query = query.order('received_at', { ascending: false });

    if (filters?.limit) {
      query = query.limit(filters.limit);
    }
    if (filters?.offset) {
      query = query.range(filters.offset, filters.offset + (filters.limit || 50) - 1);
    }

    const { data, error } = await query;
    if (error) {
      console.error('Error fetching marketing leads', error);
      return [];
    }

    return data?.map((d: any) => ({
      id: d.id,
      providerId: d.provider_id,
      source: d.source as MarketingLeadSource,
      metaLeadId: d.meta_lead_id,
      formId: d.form_id,
      status: d.status as MarketingLeadStatus,
      agencyId: d.agency_id,
      transferredAt: d.transferred_at,
      transferredBy: d.transferred_by,
      dealId: d.deal_id,
      pipelineStage: d.pipeline_stage,
      rejectionReason: d.rejection_reason,
      firstName: d.first_name,
      lastName: d.last_name,
      email: d.email,
      phone: d.phone,
      ownerId: d.owner_id,
      propertyAddress: d.property_address,
      propertyCity: d.property_city,
      propertyZipCode: d.property_zip_code,
      propertyType: d.property_type,
      propertySurface: d.property_surface,
      propertyRooms: d.property_rooms,
      projectType: d.project_type,
      annonceUrl: d.annonce_url,
      campaignId: d.campaign_id,
      campaignName: d.campaign_name,
      rawData: d.raw_data,
      receivedAt: d.received_at,
      createdAt: d.created_at,
      updatedAt: d.updated_at,
      // Champs d'assignation
      assignedToUserId: d.assigned_to_user_id,
      assignedAt: d.assigned_at,
      assignmentMethod: d.assignment_method,
      assignmentReason: d.assignment_reason,
      // Tâches et séquences
      reminders: d.reminders || [],
      sequenceSteps: d.sequence_steps || []
    })) || [];
  },

  getMarketingLead: async (leadId: string): Promise<MarketingLead | null> => {
    const { data, error } = await supabase.from('marketing_leads').select('*').eq('id', leadId).single();
    if (error || !data) {
      return null;
    }
    
    // Récupérer les notes
    const notes = data.notes ? (Array.isArray(data.notes) ? data.notes : []) : [];
    
    // Récupérer les documents
    const { data: documentsData } = await supabase
      .from('marketing_lead_documents')
      .select('*')
      .eq('lead_id', leadId)
      .order('uploaded_at', { ascending: false });
    
    const documents = documentsData?.map((d: any) => ({
      id: d.id,
      leadId: d.lead_id,
      name: d.name,
      type: d.type,
      path: d.path,
      size: d.size,
      uploadedBy: d.uploaded_by,
      uploadedAt: d.uploaded_at,
      createdAt: d.created_at
    })) || [];
    
    return {
      id: data.id,
      providerId: data.provider_id,
      source: data.source as MarketingLeadSource,
      metaLeadId: data.meta_lead_id,
      formId: data.form_id,
      status: data.status as MarketingLeadStatus,
      agencyId: data.agency_id,
      transferredAt: data.transferred_at,
      transferredBy: data.transferred_by,
      dealId: data.deal_id,
      pipelineStage: data.pipeline_stage,
      rejectionReason: data.rejection_reason,
      firstName: data.first_name,
      lastName: data.last_name,
      email: data.email,
      phone: data.phone,
      ownerId: data.owner_id,
      propertyAddress: data.property_address,
      propertyCity: data.property_city,
      propertyZipCode: data.property_zip_code,
      propertyType: data.property_type,
      propertySurface: data.property_surface,
      propertyRooms: data.property_rooms,
      projectType: data.project_type,
      annonceUrl: data.annonce_url,
      campaignId: data.campaign_id,
      campaignName: data.campaign_name,
      rawData: data.raw_data,
      notes: notes as MarketingLeadNote[],
      documents: documents,
      receivedAt: data.received_at,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      assignedToUserId: data.assigned_to_user_id,
      assignedAt: data.assigned_at,
      assignmentMethod: data.assignment_method as 'auto' | 'manual' | 'reassigned' | undefined,
      assignmentReason: data.assignment_reason,
      // Tâches et séquences
      reminders: data.reminders || [],
      sequenceSteps: data.sequence_steps || []
    };
  },

  saveMarketingLead: async (lead: Partial<MarketingLead>) => {
    const session = await supabase.auth.getSession();
    const userId = session.data.session?.user?.id;
    const normalizedReminders = (lead.reminders || []).map(normalizeReminderForSync);

    // Récupérer le provider du user courant si providerId n'est pas fourni
    let providerId = lead.providerId;
    let isSetter = false; // Pour déterminer si l'utilisateur est un setter (non superviseur)
    
    if (userId) {
      // Vérifier si l'utilisateur est un prestataire marketing
      const userProfile = await storage.getProfile(userId);
      if (userProfile && userProfile.role === 'prestataire_marketing' && !userProfile.isSupervisor) {
        isSetter = true;
      }
      
      if (!providerId) {
        const userProvider = await storage.getUserMarketingProvider(userId);
        if (userProvider) {
          providerId = userProvider.id;
        }
      }
    }

    const dbLead: any = {
      id: lead.id,
      provider_id: providerId,
      source: lead.source,
      meta_lead_id: lead.metaLeadId,
      form_id: lead.formId,
      status: lead.status,
      agency_id: lead.agencyId,
      transferred_at: lead.transferredAt,
      transferred_by: lead.transferredBy,
      deal_id: lead.dealId,
      pipeline_stage: lead.pipelineStage,
      rejection_reason: lead.rejectionReason,
      first_name: lead.firstName,
      last_name: lead.lastName,
      email: lead.email,
      phone: lead.phone,
      owner_id: lead.ownerId,
      property_address: lead.propertyAddress,
      property_city: lead.propertyCity,
      property_zip_code: lead.propertyZipCode,
      property_type: lead.propertyType,
      property_surface: lead.propertySurface,
      property_rooms: lead.propertyRooms,
      project_type: lead.projectType,
      annonce_url: lead.annonceUrl,
      campaign_id: lead.campaignId,
      campaign_name: lead.campaignName,
      raw_data: lead.rawData || {},
      notes: lead.notes || [],
      assigned_to_user_id: lead.assignedToUserId,
      assigned_at: lead.assignedAt,
      assignment_method: lead.assignmentMethod,
      assignment_reason: lead.assignmentReason,
      // Tâches et séquences
      reminders: normalizedReminders,
      sequence_steps: lead.sequenceSteps || []
    };

    // Récupérer l'ancien lead si c'est une mise à jour
    let oldLead: any = null;
    if (dbLead.id) {
      const { data: existing } = await supabase.from('marketing_leads').select('*').eq('id', dbLead.id).single();
      if (existing) oldLead = existing;
    }

    // Si c'est un setter qui crée un nouveau lead (oldLead est null) et que assigned_to_user_id n'est pas défini,
    // l'assigner automatiquement à l'utilisateur courant
    // Cela permet aux setters de voir les leads qu'ils créent (requis par la politique RLS)
    if (isSetter && !oldLead && !dbLead.assigned_to_user_id && userId) {
      dbLead.assigned_to_user_id = userId;
      dbLead.assigned_at = new Date().toISOString();
      if (!dbLead.assignment_method) {
        dbLead.assignment_method = 'manual';
      }
    }

    if (!dbLead.id) delete (dbLead as any).id;

    Object.keys(dbLead).forEach(key => dbLead[key] === undefined && delete dbLead[key]);

    const { data, error } = await supabase.from('marketing_leads').upsert(dbLead).select().single();
    if (error) console.error('Error saving marketing lead:', error);

    // Logger l'audit
    if (data && !error) {
      const actionType = oldLead ? 'UPDATE' : 'INSERT';
      await auditLogger.log(actionType, 'marketing_leads', data.id, oldLead, dbLead);
    }

    try {
      if (data?.id) {
        await googleCalendarService.syncOutbound(
          'marketing_lead',
          data.id,
          (oldLead?.reminders || []) as Reminder[],
          normalizedReminders,
        );
      }
    } catch (syncError) {
      console.error('Google Calendar outbound sync failed for saveMarketingLead', syncError);
    }

    if (data) {
      return {
        data: {
          id: data.id,
          providerId: data.provider_id,
          source: data.source as MarketingLeadSource,
          metaLeadId: data.meta_lead_id,
          formId: data.form_id,
          status: data.status as MarketingLeadStatus,
          agencyId: data.agency_id,
          transferredAt: data.transferred_at,
          transferredBy: data.transferred_by,
          dealId: data.deal_id,
          pipelineStage: data.pipeline_stage,
          rejectionReason: data.rejection_reason,
          firstName: data.first_name,
          lastName: data.last_name,
          email: data.email,
          phone: data.phone,
          ownerId: data.owner_id,
          propertyAddress: data.property_address,
          propertyCity: data.property_city,
          propertyZipCode: data.property_zip_code,
          propertyType: data.property_type,
          propertySurface: data.property_surface,
          propertyRooms: data.property_rooms,
          projectType: data.project_type,
          campaignId: data.campaign_id,
          campaignName: data.campaign_name,
          rawData: data.raw_data,
          receivedAt: data.received_at,
          createdAt: data.created_at,
          updatedAt: data.updated_at
        },
        error: null
      };
    }
    return { data: null, error };
  },

  updateMarketingLead: async (leadId: string, updates: Partial<MarketingLead>) => {
    const { data: oldLead } = await supabase.from('marketing_leads').select('*').eq('id', leadId).single();

    if (!oldLead) {
      return { data: null, error: new Error('Lead non trouvé') };
    }

    const dbUpdates: any = {};
    if (updates.status !== undefined) dbUpdates.status = updates.status;
    if (updates.firstName !== undefined) dbUpdates.first_name = updates.firstName;
    if (updates.lastName !== undefined) dbUpdates.last_name = updates.lastName;
    if (updates.email !== undefined) dbUpdates.email = updates.email;
    if (updates.phone !== undefined) dbUpdates.phone = updates.phone;
    if (updates.propertyAddress !== undefined) dbUpdates.property_address = updates.propertyAddress;
    if (updates.propertyCity !== undefined) dbUpdates.property_city = updates.propertyCity;
    if (updates.propertyZipCode !== undefined) dbUpdates.property_zip_code = updates.propertyZipCode;
    if (updates.propertyType !== undefined) dbUpdates.property_type = updates.propertyType;
    if (updates.propertySurface !== undefined) dbUpdates.property_surface = updates.propertySurface;
    if (updates.propertyRooms !== undefined) dbUpdates.property_rooms = updates.propertyRooms;
    if (updates.projectType !== undefined) dbUpdates.project_type = updates.projectType;
    if (updates.annonceUrl !== undefined) dbUpdates.annonce_url = updates.annonceUrl;
    if (updates.rejectionReason !== undefined) dbUpdates.rejection_reason = updates.rejectionReason;
    if (updates.assignedToUserId !== undefined) dbUpdates.assigned_to_user_id = updates.assignedToUserId;
    if (updates.assignedAt !== undefined) dbUpdates.assigned_at = updates.assignedAt;
    if (updates.assignmentMethod !== undefined) dbUpdates.assignment_method = updates.assignmentMethod;
    if (updates.assignmentReason !== undefined) dbUpdates.assignment_reason = updates.assignmentReason;
    if (updates.agencyId !== undefined) dbUpdates.agency_id = updates.agencyId;
    // Champs JSON
    if (updates.reminders !== undefined) dbUpdates.reminders = updates.reminders.map(normalizeReminderForSync);
    if (updates.sequenceSteps !== undefined) dbUpdates.sequence_steps = updates.sequenceSteps;
    if (updates.notes !== undefined) dbUpdates.notes = updates.notes;

    const { data, error } = await supabase
      .from('marketing_leads')
      .update(dbUpdates)
      .eq('id', leadId)
      .select()
      .single();

    if (error) {
      console.error('Error updating marketing lead', error);
      return { data: null, error };
    }

    // Logger l'audit
    if (data && !error && oldLead) {
      await auditLogger.logUpdate('marketing_leads', leadId, oldLead, { ...oldLead, ...dbUpdates });
    }

    // Sync sortante Google Calendar (non bloquante)
    try {
      if (updates.reminders !== undefined) {
        await googleCalendarService.syncOutbound(
          'marketing_lead',
          leadId,
          (oldLead.reminders || []) as Reminder[],
          (dbUpdates.reminders || []) as Reminder[],
        );
      }
    } catch (syncError) {
      console.error('Google Calendar outbound sync failed for marketing lead', syncError);
    }

    if (data) {
      return {
        data: {
          id: data.id,
          providerId: data.provider_id,
          source: data.source as MarketingLeadSource,
          metaLeadId: data.meta_lead_id,
          formId: data.form_id,
          status: data.status as MarketingLeadStatus,
          agencyId: data.agency_id,
          transferredAt: data.transferred_at,
          transferredBy: data.transferred_by,
          dealId: data.deal_id,
          pipelineStage: data.pipeline_stage,
          rejectionReason: data.rejection_reason,
          firstName: data.first_name,
          lastName: data.last_name,
          email: data.email,
          phone: data.phone,
          ownerId: data.owner_id,
          propertyAddress: data.property_address,
          propertyCity: data.property_city,
          propertyZipCode: data.property_zip_code,
          propertyType: data.property_type,
          propertySurface: data.property_surface,
          propertyRooms: data.property_rooms,
          projectType: data.project_type,
          campaignId: data.campaign_id,
          campaignName: data.campaign_name,
          rawData: data.raw_data,
          receivedAt: data.received_at,
          createdAt: data.created_at,
          updatedAt: data.updated_at,
          assignedToUserId: data.assigned_to_user_id,
          assignedAt: data.assigned_at,
          assignmentMethod: data.assignment_method as 'auto' | 'manual' | 'reassigned' | undefined,
          assignmentReason: data.assignment_reason,
          notes: data.notes || [],
          reminders: data.reminders || [],
          sequenceSteps: data.sequence_steps || []
        },
        error: null
      };
    }
    return { data: null, error };
  },

  updateMarketingLeadStatus: async (
    leadId: string,
    newStatus: MarketingLeadStatus,
    comment?: string
  ): Promise<{ data: MarketingLead | null; error: any }> => {
    const session = await supabase.auth.getSession();
    const userId = session.data.session?.user?.id;

    const { data: oldLead } = await supabase.from('marketing_leads').select('*').eq('id', leadId).single();

    if (!oldLead) {
      return { data: null, error: new Error('Lead non trouvé') };
    }

    const { data, error } = await supabase
      .from('marketing_leads')
      .update({ status: newStatus })
      .eq('id', leadId)
      .select()
      .single();

    if (error) {
      console.error('Error updating marketing lead status', error);
      return { data: null, error };
    }

    // Ajouter un commentaire à l'historique si fourni
    if (comment && userId) {
      await supabase.from('marketing_lead_status_history').insert({
        lead_id: leadId,
        old_status: oldLead.status,
        new_status: newStatus,
        changed_by: userId,
        comment: comment
      });
    }

    // Logger l'audit
    if (data && !error && oldLead) {
      await auditLogger.logUpdate('marketing_leads', leadId, oldLead, { ...oldLead, status: newStatus });
    }

    if (data) {
      return {
        data: {
          id: data.id,
          providerId: data.provider_id,
          source: data.source as MarketingLeadSource,
          metaLeadId: data.meta_lead_id,
          formId: data.form_id,
          status: data.status as MarketingLeadStatus,
          agencyId: data.agency_id,
          transferredAt: data.transferred_at,
          transferredBy: data.transferred_by,
          dealId: data.deal_id,
          pipelineStage: data.pipeline_stage,
          rejectionReason: data.rejection_reason,
          firstName: data.first_name,
          lastName: data.last_name,
          email: data.email,
          phone: data.phone,
          ownerId: data.owner_id,
          propertyAddress: data.property_address,
          propertyCity: data.property_city,
          propertyZipCode: data.property_zip_code,
          propertyType: data.property_type,
          propertySurface: data.property_surface,
          propertyRooms: data.property_rooms,
          projectType: data.project_type,
          campaignId: data.campaign_id,
          campaignName: data.campaign_name,
          rawData: data.raw_data,
          receivedAt: data.received_at,
          createdAt: data.created_at,
          updatedAt: data.updated_at,
          assignedToUserId: data.assigned_to_user_id,
          assignedAt: data.assigned_at,
          assignmentMethod: data.assignment_method as 'auto' | 'manual' | 'reassigned' | undefined,
          assignmentReason: data.assignment_reason
        },
        error: null
      };
    }
    return { data: null, error };
  },

  assignMarketingLeadToNearestSetter: async (
    leadId: string,
    providerId: string,
    zipCode: string
  ): Promise<{ data: { assignedToUserId: string | null; agencyId: string | null } | null; error: any }> => {
    try {
      // Appeler la fonction SQL
      const { data, error } = await supabase.rpc('assign_lead_to_nearest_setter', {
        p_lead_id: leadId,
        p_provider_id: providerId,
        p_lead_zip_code: zipCode
      });

      if (error) {
        console.error('Error assigning lead to nearest setter:', error);
        return { data: null, error };
      }

      // Si pas d'affectation (NULL), retourner avec agencyId null
      if (!data) {
        return {
          data: { assignedToUserId: null, agencyId: null },
          error: null
        };
      }

      // Récupérer l'agence du setter assigné
      const { data: setterData } = await supabase
        .from('marketing_provider_user_agencies')
        .select('agency_id')
        .eq('user_id', data)
        .limit(1)
        .maybeSingle();

      // Envoyer email de notification au setter si assigné
      if (data) {
        try {
          const lead = await storage.getMarketingLead(leadId);
          if (lead) {
            const setterProfile = await storage.getProfile(data);
            if (setterProfile?.email) {
              const { emailService } = await import('./email');
              await emailService.sendLeadAssignmentToSetterNotification(
                setterProfile.email,
                lead,
                'auto'
              );
            }
          }
        } catch (emailError) {
          console.error('Error sending lead assignment notification to setter', emailError);
          // Ne pas bloquer l'affectation si l'email échoue
        }
      }

      return {
        data: {
          assignedToUserId: data,
          agencyId: setterData?.agency_id || null
        },
        error: null
      };
    } catch (error: any) {
      console.error('Error in assignMarketingLeadToNearestSetter:', error);
      return { data: null, error };
    }
  },

  reassignMarketingLead: async (
    leadId: string,
    newSetterUserId: string,
    reason: string,
    reassignedByUserId: string
  ): Promise<{ data: any; error: any }> => {
    try {
      // Récupérer l'ancien lead
      const { data: oldLead } = await supabase
        .from('marketing_leads')
        .select('assigned_to_user_id')
        .eq('id', leadId)
        .single();

      if (!oldLead) {
        return { data: null, error: new Error('Lead non trouvé') };
      }

      const previousAssignedToUserId = oldLead.assigned_to_user_id;

      // Mettre à jour le lead
      const { data: updatedLead, error: updateError } = await supabase
        .from('marketing_leads')
        .update({
          assigned_to_user_id: newSetterUserId,
          assigned_at: new Date().toISOString(),
          assignment_method: 'reassigned',
          assignment_reason: reason
        })
        .eq('id', leadId)
        .select()
        .single();

      if (updateError) {
        console.error('Error reassigning lead:', updateError);
        return { data: null, error: updateError };
      }

      // Insérer dans l'historique
      const { error: historyError } = await supabase
        .from('marketing_lead_assignments')
        .insert({
          lead_id: leadId,
          assigned_to_user_id: newSetterUserId,
          assigned_by_user_id: reassignedByUserId,
          assignment_method: 'reassigned',
          reason: reason,
          previous_assigned_to_user_id: previousAssignedToUserId
        });

      if (historyError) {
        console.error('Error creating assignment history:', historyError);
        // Ne pas échouer si l'historique ne peut pas être créé
      }

      // Logger l'audit
      if (updatedLead && oldLead) {
        await auditLogger.logUpdate('marketing_leads', leadId, oldLead, {
          ...oldLead,
          assigned_to_user_id: newSetterUserId,
          assigned_at: new Date().toISOString(),
          assignment_method: 'reassigned',
          assignment_reason: reason
        });
      }

      // Envoyer email de notification au setter
      if (updatedLead && newSetterUserId) {
        try {
          const lead = await storage.getMarketingLead(leadId);
          if (lead) {
            const setterProfile = await storage.getProfile(newSetterUserId);
            const reassignedByProfile = await storage.getProfile(reassignedByUserId);
            const reassignedByName = reassignedByProfile 
              ? (reassignedByProfile.firstName && reassignedByProfile.lastName 
                  ? `${reassignedByProfile.firstName} ${reassignedByProfile.lastName}`
                  : reassignedByProfile.email || 'Superviseur')
              : 'Superviseur';

            if (setterProfile?.email) {
              const { emailService } = await import('./email');
              await emailService.sendLeadAssignmentToSetterNotification(
                setterProfile.email,
                lead,
                'reassigned',
                reassignedByName,
                reason
              );
            }
          }
        } catch (emailError) {
          console.error('Error sending lead assignment notification to setter', emailError);
          // Ne pas bloquer la réaffectation si l'email échoue
        }
      }

      return {
        data: updatedLead,
        error: null
      };
    } catch (error: any) {
      console.error('Error in reassignMarketingLead:', error);
      return { data: null, error };
    }
  },

  getMarketingLeadStatusHistory: async (leadId: string): Promise<MarketingLeadStatusHistory[]> => {
    const { data, error } = await supabase
      .from('marketing_lead_status_history')
      .select('*')
      .eq('lead_id', leadId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching marketing lead status history', error);
      return [];
    }

    return data?.map((d: any) => ({
      id: d.id,
      leadId: d.lead_id,
      oldStatus: d.old_status,
      newStatus: d.new_status,
      changedBy: d.changed_by,
      comment: d.comment,
      createdAt: d.created_at
    })) || [];
  },

  getMarketingLeadAssignmentHistory: async (leadId: string): Promise<Array<{
    id: string;
    leadId: string;
    assignedToUserId: string | null;
    assignedByUserId: string | null;
    assignmentMethod: 'auto' | 'manual' | 'reassigned';
    reason: string | null;
    previousAssignedToUserId: string | null;
    createdAt: string;
  }>> => {
    const { data, error } = await supabase
      .from('marketing_lead_assignments')
      .select('*')
      .eq('lead_id', leadId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching marketing lead assignment history', error);
      return [];
    }

    return data?.map((d: any) => ({
      id: d.id,
      leadId: d.lead_id,
      assignedToUserId: d.assigned_to_user_id,
      assignedByUserId: d.assigned_by_user_id,
      assignmentMethod: d.assignment_method,
      reason: d.reason,
      previousAssignedToUserId: d.previous_assigned_to_user_id,
      createdAt: d.created_at
    })) || [];
  },

  getMarketingLeadStats: async (filters?: {
    startDate?: string;
    endDate?: string;
    campaignId?: string;
    providerId?: string;
  }): Promise<MarketingLeadStats | null> => {
    let query = supabase.from('marketing_leads').select('status, source, campaign_id, campaign_name, rejection_reason, provider_id, assigned_to_user_id');

    if (filters?.startDate) {
      query = query.gte('received_at', filters.startDate);
    }
    if (filters?.endDate) {
      query = query.lte('received_at', filters.endDate);
    }
    if (filters?.campaignId) {
      query = query.eq('campaign_id', filters.campaignId);
    }
    if (filters?.providerId) {
      query = query.eq('provider_id', filters.providerId);
    }

    const { data, error } = await query;
    if (error) {
      console.error('Error fetching marketing lead stats', error);
      return null;
    }

    if (!data || data.length === 0) {
      return {
        totalLeads: 0,
        byStatus: {
          nouveau: 0,
          en_qualification: 0,
          qualifie: 0,
          transfere: 0,
          rejete: 0
        },
        bySource: {
          meta: 0,
          manual: 0,
          call: 0,
          other: 0,
          estimation_form: 0,
          leboncoin: 0,
          pap: 0,
          seloger: 0
        },
        byCampaign: [],
        bySetter: [],
        qualificationRate: 0,
        rejectionRate: 0,
        rejectionReasons: []
      };
    }

    const byStatus: Record<MarketingLeadStatus, number> = {
      nouveau: 0,
      en_qualification: 0,
      qualifie: 0,
      transfere: 0,
      rejete: 0
    };

    const bySource: Record<MarketingLeadSource, number> = {
      meta: 0,
      manual: 0,
      call: 0,
      other: 0,
      estimation_form: 0,
      leboncoin: 0,
      pap: 0,
      seloger: 0
    };

    const campaignMap = new Map<string, { campaignId: string; campaignName: string; count: number }>();
    const rejectionReasonsMap = new Map<string, number>();
    const setterMap = new Map<string, {
      userId: string;
      count: number;
      byStatus: Record<MarketingLeadStatus, number>;
      qualified: number;
      rejected: number;
    }>();

    let totalReceived = 0;
    let totalQualified = 0;
    let totalRejected = 0;

    data.forEach((lead: any) => {
      const status = lead.status as MarketingLeadStatus;
      const source = lead.source as MarketingLeadSource;
      byStatus[status] = (byStatus[status] || 0) + 1;
      bySource[source] = (bySource[source] || 0) + 1;

      totalReceived++;
      if (status === 'qualifie') totalQualified++;
      if (status === 'rejete') totalRejected++;

      if (lead.campaign_id && lead.campaign_name) {
        const key = lead.campaign_id;
        const existing = campaignMap.get(key) || { campaignId: lead.campaign_id, campaignName: lead.campaign_name, count: 0 };
        existing.count++;
        campaignMap.set(key, existing);
      }

      if (lead.rejection_reason) {
        rejectionReasonsMap.set(lead.rejection_reason, (rejectionReasonsMap.get(lead.rejection_reason) || 0) + 1);
      }

      // Stats par setter
      if (lead.assigned_to_user_id) {
        const setterId = lead.assigned_to_user_id;
        let setterStats = setterMap.get(setterId);
        if (!setterStats) {
          setterStats = {
            userId: setterId,
            count: 0,
            byStatus: {
              nouveau: 0,
              en_qualification: 0,
              qualifie: 0,
              transfere: 0,
              rejete: 0
            },
            qualified: 0,
            rejected: 0
          };
        }
        setterStats.count++;
        setterStats.byStatus[status] = (setterStats.byStatus[status] || 0) + 1;
        if (status === 'qualifie') setterStats.qualified++;
        if (status === 'rejete') setterStats.rejected++;
        setterMap.set(setterId, setterStats);
      }
    });

    // Récupérer les noms des setters depuis les profils
    const setterIds = Array.from(setterMap.keys());
    const setterProfiles: Map<string, string> = new Map();
    
    if (setterIds.length > 0) {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email')
        .in('id', setterIds);
      
      if (profiles) {
        profiles.forEach((profile: any) => {
          const userName = profile.first_name && profile.last_name
            ? `${profile.first_name} ${profile.last_name}`
            : profile.email || 'Utilisateur inconnu';
          setterProfiles.set(profile.id, userName);
        });
      }
    }

    // Construire le tableau bySetter avec les noms
    const bySetter = Array.from(setterMap.entries()).map(([userId, stats]) => ({
      userId,
      userName: setterProfiles.get(userId) || 'Utilisateur inconnu',
      count: stats.count,
      byStatus: stats.byStatus,
      qualificationRate: stats.count > 0 ? (stats.qualified / stats.count) * 100 : 0,
      rejectionRate: stats.count > 0 ? (stats.rejected / stats.count) * 100 : 0
    })).sort((a, b) => b.count - a.count); // Trier par nombre de leads décroissant

    return {
      totalLeads: data.length,
      byStatus,
      bySource,
      byCampaign: Array.from(campaignMap.values()),
      bySetter,
      qualificationRate: totalReceived > 0 ? (totalQualified / totalReceived) * 100 : 0,
      rejectionRate: totalReceived > 0 ? (totalRejected / totalReceived) * 100 : 0,
      rejectionReasons: Array.from(rejectionReasonsMap.entries()).map(([reason, count]) => ({ reason, count }))
    };
  },

  // --- MARKETING LEAD NOTES & DOCUMENTS ---
  addMarketingLeadNote: async (leadId: string, noteContent: string): Promise<{ data: MarketingLeadNote | null; error: any }> => {
    const session = await supabase.auth.getSession();
    const userId = session.data.session?.user?.id;
    
    // Récupérer le lead actuel
    const { data: leadData } = await supabase.from('marketing_leads').select('notes').eq('id', leadId).single();
    const currentNotes = leadData?.notes ? (Array.isArray(leadData.notes) ? leadData.notes : []) : [];
    
    // Récupérer le profil de l'utilisateur pour le nom
    let authorName = 'Utilisateur';
    if (userId) {
      const profile = await storage.getProfile(userId);
      if (profile) {
        authorName = profile.firstName && profile.lastName
          ? `${profile.firstName} ${profile.lastName}`
          : profile.email || 'Utilisateur';
      }
    }
    
    const newNote: MarketingLeadNote = {
      id: Date.now().toString(),
      content: noteContent,
      createdAt: new Date().toISOString(),
      authorName: authorName,
      authorId: userId || null
    };
    
    const updatedNotes = [...currentNotes, newNote];
    
    const { data, error } = await supabase
      .from('marketing_leads')
      .update({ notes: updatedNotes })
      .eq('id', leadId)
      .select()
      .single();
    
    if (error) {
      console.error('Error adding note to marketing lead:', error);
      return { data: null, error };
    }
    
    return { data: newNote, error: null };
  },

  deleteMarketingLeadNote: async (leadId: string, noteId: string): Promise<{ error: any }> => {
    const { data: leadData } = await supabase.from('marketing_leads').select('notes').eq('id', leadId).single();
    const currentNotes = leadData?.notes ? (Array.isArray(leadData.notes) ? leadData.notes : []) : [];
    
    const updatedNotes = currentNotes.filter((note: any) => note.id !== noteId);
    
    const { error } = await supabase
      .from('marketing_leads')
      .update({ notes: updatedNotes })
      .eq('id', leadId);
    
    if (error) {
      console.error('Error deleting note from marketing lead:', error);
      return { error };
    }
    
    return { error: null };
  },

  uploadMarketingLeadDocument: async (leadId: string, file: File): Promise<{ data: MarketingLeadDocument | null; error: any }> => {
    const session = await supabase.auth.getSession();
    const userId = session.data.session?.user?.id;
    
    // Générer un nom de fichier unique
    const fileExt = file.name.split('.').pop();
    const fileName = `${leadId}/${Date.now()}.${fileExt}`;
    const filePath = `marketing-leads/${fileName}`;
    
    // Upload vers Supabase Storage
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('documents')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });
    
    if (uploadError) {
      console.error('Error uploading document:', uploadError);
      return { data: null, error: uploadError };
    }
    
    // Créer l'entrée en base
    const { data, error } = await supabase
      .from('marketing_lead_documents')
      .insert({
        lead_id: leadId,
        name: file.name,
        type: fileExt || 'unknown',
        path: filePath,
        size: file.size,
        uploaded_by: userId || null
      })
      .select()
      .single();
    
    if (error) {
      console.error('Error creating document entry:', error);
      // Supprimer le fichier uploadé en cas d'erreur
      await supabase.storage.from('documents').remove([filePath]);
      return { data: null, error };
    }
    
    return {
      data: {
        id: data.id,
        leadId: data.lead_id,
        name: data.name,
        type: data.type,
        path: data.path,
        size: data.size,
        uploadedBy: data.uploaded_by,
        uploadedAt: data.uploaded_at,
        createdAt: data.created_at
      },
      error: null
    };
  },

  deleteMarketingLeadDocument: async (documentId: string): Promise<{ error: any }> => {
    // Récupérer le document pour obtenir le path
    const { data: document } = await supabase
      .from('marketing_lead_documents')
      .select('path')
      .eq('id', documentId)
      .single();
    
    if (!document) {
      return { error: new Error('Document not found') };
    }
    
    // Supprimer le fichier du storage
    await supabase.storage.from('documents').remove([document.path]);
    
    // Supprimer l'entrée en base
    const { error } = await supabase
      .from('marketing_lead_documents')
      .delete()
      .eq('id', documentId);
    
    if (error) {
      console.error('Error deleting document:', error);
      return { error };
    }
    
    return { error: null };
  },

  getMarketingLeadDocumentUrl: async (documentPath: string): Promise<string | null> => {
    const { data } = await supabase.storage
      .from('documents')
      .createSignedUrl(documentPath, 3600); // URL valide 1h
    
    return data?.signedUrl || null;
  },

  transferMarketingLeadToAgency: async (
    leadId: string,
    agencyId: string,
    pipelineStage: DealStage,
    hasAppointment: boolean = false,
    appointmentDate?: string,
    appointmentNotes?: string
  ): Promise<{ data: MarketingLead | null; error: any; dealId?: string }> => {
    const session = await supabase.auth.getSession();
    const userId = session.data.session?.user?.id;

    const lead = await storage.getMarketingLead(leadId);
    if (!lead) {
      return { data: null, error: new Error('Lead introuvable') };
    }

    if (lead.status !== 'qualifie') {
      return { 
        data: null, 
        error: new Error('Seuls les leads qualifiés peuvent être transférés') 
      };
    }

    // Log pour vérifier que le lead a bien un providerId
    console.log('[transferMarketingLeadToAgency] Lead retrieved', {
      leadId: lead.id,
      leadProviderId: lead.providerId,
      leadSource: lead.source,
      hasProviderId: !!lead.providerId
    });

    // Si le lead n'a pas de providerId, essayer de le récupérer directement depuis la base
    let finalProviderId = lead.providerId;
    if (!finalProviderId) {
      const { data: leadData } = await supabase
        .from('marketing_leads')
        .select('provider_id')
        .eq('id', leadId)
        .single();
      
      if (leadData?.provider_id) {
        finalProviderId = leadData.provider_id;
        console.log('[transferMarketingLeadToAgency] Retrieved providerId from DB', {
          providerId: finalProviderId
        });
      } else {
        console.warn('[transferMarketingLeadToAgency] Lead has no providerId', {
          leadId: lead.id,
          leadSource: lead.source
        });
      }
    }

    try {
      const dealType: PipelineType = lead.projectType?.toLowerCase().includes('conciergerie') 
        ? PipelineType.CONCIERGE 
        : PipelineType.SUBLET;

      // Récupérer le nom du prestataire marketing pour la source
      let providerNameForSource = '';
      if (finalProviderId) {
        const provider = await storage.getMarketingProvider(finalProviderId);
        if (provider) {
          providerNameForSource = provider.name;
        }
      }

      // Construire la source avec le nom du provider
      let ownerSource: string;
      if (lead.source === 'meta') {
        ownerSource = providerNameForSource ? `Meta/${providerNameForSource}` : 'Meta';
      } else {
        const sourceLabel = lead.source === 'call' ? 'Appel' : lead.source === 'manual' ? 'Manuel' : lead.source === 'estimation_form' ? 'Estimation' : lead.source;
        ownerSource = providerNameForSource ? `${sourceLabel}/${providerNameForSource}` : sourceLabel;
      }

      const owner: Owner = {
        id: lead.ownerId || undefined, // Inclure l'ID si le lead a déjà un propriétaire
        firstName: lead.firstName || 'À définir',
        lastName: lead.lastName || '',
        email: lead.email || '',
        phone: lead.phone || '',
        address: lead.propertyAddress || '',
        city: lead.propertyCity || '',
        zipCode: lead.propertyZipCode || '',
        source: ownerSource as LeadSource,
        agencyId: agencyId
      };

      // Toujours sauvegarder le propriétaire (création ou mise à jour)
      const { data: ownerData, error: ownerError } = await storage.saveOwner(owner);
      if (ownerError || !ownerData?.id) {
        console.error('Error saving owner from marketing lead', ownerError, { leadId, ownerId: lead.ownerId });
        return { 
          data: null, 
          error: new Error('Erreur lors de la sauvegarde du propriétaire: ' + (ownerError?.message || 'Unknown')) 
        };
      }
      const finalOwnerId = ownerData.id;

      if (!lead.propertyAddress || !lead.propertyCity) {
        return { 
          data: null, 
          error: new Error('Adresse du bien requise pour créer un deal') 
        };
      }

      const property: Property = {
        ownerId: finalOwnerId,
        address: lead.propertyAddress,
        city: lead.propertyCity,
        zipCode: lead.propertyZipCode || undefined,
        type: (lead.propertyType as 'Appartement' | 'Maison' | 'Immeuble' | 'Autre') || 'Appartement',
        surface: lead.propertySurface || 0,
        bedrooms: lead.propertyRooms || 0,
        agencyId: agencyId,
        status: 'PROSPECT'
      };

      const { data: propertyData, error: propertyError } = await storage.saveProperty(property);
      if (propertyError || !propertyData?.id) {
        console.error('Error creating property from marketing lead', propertyError, { leadId });
        return { 
          data: null, 
          error: new Error('Erreur lors de la création du bien: ' + (propertyError?.message || 'Unknown')) 
        };
      }

      // Récupérer le nom de l'utilisateur qui transfère le lead
      let transferredByUserName = 'Système';
      if (userId) {
        const transfererProfile = await storage.getProfile(userId);
        if (transfererProfile) {
          transferredByUserName = transfererProfile.firstName && transfererProfile.lastName
            ? `${transfererProfile.firstName} ${transfererProfile.lastName}`
            : transfererProfile.email || 'Utilisateur';
        }
      }

      // Récupérer le nom de l'agence
      const agencies = await storage.getAllAgencies();
      const assignedAgency = agencies.find(a => a.id === agencyId);
      const agencyName = assignedAgency ? assignedAgency.name || assignedAgency.city : 'Agence inconnue';

      // Récupérer le nom du prestataire marketing
      let providerName = 'Prestataire marketing';
      if (finalProviderId) {
        const provider = await storage.getMarketingProvider(finalProviderId);
        if (provider) {
          providerName = provider.name;
        }
      }

      const sourceLabel = lead.source === 'meta' ? 'Meta' : 
                         lead.source === 'manual' ? 'Manuel' : 
                         lead.source === 'call' ? 'Appel' :
                         lead.source === 'estimation_form' ? 'Formulaire Estimation' :
                         lead.source === 'leboncoin' ? 'Leboncoin' :
                         lead.source === 'pap' ? 'PAP' :
                         lead.source === 'other' ? 'Autre' : lead.source;
      
      const leadNote = {
        id: Date.now().toString(),
        content: `📱 Lead Marketing (${sourceLabel})\n` +
          `Date de réception: ${new Date(lead.receivedAt).toLocaleString('fr-FR')}\n` +
          (lead.campaignName ? `Campagne: ${lead.campaignName}\n` : '') +
          (lead.projectType ? `Type de projet: ${lead.projectType}\n` : '') +
          `Transféré à: ${agencyName}\n` +
          `Transféré par: ${transferredByUserName} (${providerName})\n` +
          `Pipeline: ${pipelineStage}`,
        createdAt: new Date().toISOString(),
        authorName: 'Système'
      };

      // Créer le reminder si rendez-vous
      const reminders: Reminder[] = [];
      if (hasAppointment && appointmentDate) {
        reminders.push({
          id: Date.now().toString(),
          date: appointmentDate,
          note: appointmentNotes || 'Rendez-vous avec le propriétaire',
          isDone: false
        });
      }

      // Log pour déboguer le transfert
      console.log('[transferMarketingLeadToAgency] Creating deal from lead', {
        leadId: lead.id,
        leadProviderId: lead.providerId,
        leadSource: lead.source,
        agencyId
      });

      const deal: Deal = {
        agencyId: agencyId,
        propertyId: propertyData.id,
        ownerId: finalOwnerId,
        type: dealType,
        stage: pipelineStage,
        value: 0,
        source: ownerSource as LeadSource, // Utiliser la même source que le propriétaire
        notes: [leadNote],
        reminders: reminders.length > 0 ? reminders : undefined,
        marketingProviderId: finalProviderId || null,
        marketingLeadId: lead.id || null
      };

      console.log('[transferMarketingLeadToAgency] Deal to be created', {
        dealMarketingProviderId: deal.marketingProviderId,
        dealMarketingLeadId: deal.marketingLeadId
      });

      const { data: createdDeal, error: dealError } = await storage.saveDeal(deal);
      if (dealError || !createdDeal) {
        console.error('Error creating deal from marketing lead', dealError, { leadId });
        return { 
          data: null, 
          error: new Error('Erreur lors de la création du deal: ' + (dealError?.message || 'Unknown')) 
        };
      }

      console.log('[transferMarketingLeadToAgency] Deal created successfully', {
        dealId: createdDeal.id,
        savedMarketingProviderId: createdDeal.marketingProviderId,
        savedMarketingLeadId: createdDeal.marketingLeadId
      });

      // Mettre à jour le lead
      const { data: updatedLead, error: updateError } = await supabase
        .from('marketing_leads')
        .update({
          agency_id: agencyId,
          status: 'transfere',
          transferred_at: new Date().toISOString(),
          transferred_by: userId || null,
          deal_id: createdDeal.id,
          pipeline_stage: pipelineStage,
          owner_id: finalOwnerId
        })
        .eq('id', leadId)
        .select()
        .single();

      if (updateError) {
        console.error('Error updating marketing lead', updateError, { leadId, agencyId });
        return { data: null, error: updateError };
      }

      // Créer le commentaire pour l'historique
      const historyComment = `Lead transféré à ${agencyName} par ${transferredByUserName} et converti en deal (ID: ${createdDeal.id || 'N/A'})${hasAppointment && appointmentDate ? ` - Rendez-vous prévu le ${new Date(appointmentDate).toLocaleDateString('fr-FR')}` : ''}`;
      
      await supabase.from('marketing_lead_status_history').insert({
        lead_id: leadId,
        old_status: 'qualifie',
        new_status: 'transfere',
        changed_by: userId || null,
        comment: historyComment
      });

      if (!updatedLead) return { data: null, error: null };

      const mapped: MarketingLead = {
        id: updatedLead.id,
        providerId: updatedLead.provider_id,
        source: updatedLead.source as MarketingLeadSource,
        metaLeadId: updatedLead.meta_lead_id,
        formId: updatedLead.form_id,
        status: updatedLead.status as MarketingLeadStatus,
        agencyId: updatedLead.agency_id,
        transferredAt: updatedLead.transferred_at,
        transferredBy: updatedLead.transferred_by,
        dealId: updatedLead.deal_id,
        pipelineStage: updatedLead.pipeline_stage,
        rejectionReason: updatedLead.rejection_reason,
        firstName: updatedLead.first_name,
        lastName: updatedLead.last_name,
        email: updatedLead.email,
        phone: updatedLead.phone,
        ownerId: updatedLead.owner_id,
        propertyAddress: updatedLead.property_address,
        propertyCity: updatedLead.property_city,
        propertyZipCode: updatedLead.property_zip_code,
        propertyType: updatedLead.property_type,
        propertySurface: updatedLead.property_surface,
        propertyRooms: updatedLead.property_rooms,
        projectType: updatedLead.project_type,
        campaignId: updatedLead.campaign_id,
        campaignName: updatedLead.campaign_name,
        rawData: updatedLead.raw_data,
        receivedAt: updatedLead.received_at,
        createdAt: updatedLead.created_at,
        updatedAt: updatedLead.updated_at
      };

      // Log pour vérifier l'état de l'agence avant l'envoi d'email
      console.log('[transferMarketingLeadToAgency] Checking agency before email send', {
        agencyId,
        assignedAgencyFound: !!assignedAgency,
        agencyName: assignedAgency?.name || 'NOT FOUND',
        agencyEmail: assignedAgency?.email || 'NO EMAIL',
        hasEmail: !!assignedAgency?.email
      });

      // Envoyer email à l'agence
      if (assignedAgency?.email) {
        console.log('[transferMarketingLeadToAgency] Attempting to send email notification', {
          to: assignedAgency.email,
          leadId: mapped.id,
          leadName: `${mapped.firstName} ${mapped.lastName}`,
          providerName,
          pipelineStage
        });
        
        try {
          const { emailService } = await import('./email');
          const emailResult = await emailService.sendLeadTransferNotification(
            assignedAgency.email,
            mapped,
            providerName,
            pipelineStage,
            hasAppointment,
            appointmentDate
          );
          
          console.log('[transferMarketingLeadToAgency] Email sent successfully', {
            emailResult,
            to: assignedAgency.email
          });
        } catch (emailError) {
          console.error('[transferMarketingLeadToAgency] Error sending lead transfer notification email', {
            error: emailError,
            errorMessage: emailError instanceof Error ? emailError.message : String(emailError),
            errorStack: emailError instanceof Error ? emailError.stack : undefined,
            to: assignedAgency.email,
            leadId: mapped.id
          });
          // Ne pas bloquer le transfert si l'email échoue
        }
      } else {
        console.warn('[transferMarketingLeadToAgency] ⚠️ Agency has no email, skipping notification', {
          agencyId,
          agencyName: assignedAgency?.name || 'Agency not found',
          assignedAgencyFound: !!assignedAgency
        });
      }

      return { data: mapped, error: null, dealId: createdDeal.id };

    } catch (error: any) {
      console.error('Error transferring marketing lead to agency', error, { leadId, agencyId });
      return { 
        data: null, 
        error: new Error('Erreur lors du transfert: ' + (error?.message || 'Unknown')) 
      };
    }
  },

  resendLeadTransferEmail: async (leadId: string): Promise<{ success: boolean; error?: any }> => {
    try {
      // Récupérer le lead
      const lead = await storage.getMarketingLead(leadId);
      if (!lead) {
        return { success: false, error: new Error('Lead introuvable') };
      }

      if (lead.status !== 'transfere' || !lead.agencyId) {
        return { success: false, error: new Error('Ce lead n\'a pas été transféré à une agence') };
      }

      // Récupérer l'agence
      const agencies = await storage.getAllAgencies();
      const assignedAgency = agencies.find(a => a.id === lead.agencyId);
      
      if (!assignedAgency) {
        return { success: false, error: new Error('Agence introuvable') };
      }

      if (!assignedAgency.email) {
        return { success: false, error: new Error('L\'agence n\'a pas d\'email configuré') };
      }

      // Récupérer le nom du prestataire marketing
      let providerName = 'Prestataire marketing';
      if (lead.providerId) {
        const provider = await storage.getMarketingProvider(lead.providerId);
        if (provider) {
          providerName = provider.name;
        }
      }

      // Récupérer le pipeline stage depuis le lead
      const pipelineStage = lead.pipelineStage || 'Nouveau';

      // Envoyer l'email
      const { emailService } = await import('./email');
      await emailService.sendLeadTransferNotification(
        assignedAgency.email,
        lead,
        providerName,
        pipelineStage,
        false, // hasAppointment - on ne peut pas le savoir depuis le lead seul
        undefined // appointmentDate
      );

      console.log('[resendLeadTransferEmail] Email sent successfully', {
        leadId,
        agencyEmail: assignedAgency.email,
        agencyName: assignedAgency.name
      });

      return { success: true };
    } catch (error: any) {
      console.error('[resendLeadTransferEmail] Error resending email', error);
      return { success: false, error };
    }
  },

  // --- ESTIMATION STATS ---
  getEstimationStats: async (startDate?: string, endDate?: string) => {
    // Get AI usage for ESTIMATE_REVENUE (public mode = user_id is null)
    let usageQuery = supabase
      .from('ai_usage')
      .select('id, created_at, input_tokens, output_tokens, total_tokens, cost_usd')
      .eq('usage_type', 'ESTIMATE_REVENUE')
      .is('user_id', null);

    if (startDate) {
      usageQuery = usageQuery.gte('created_at', startDate);
    }
    if (endDate) {
      usageQuery = usageQuery.lte('created_at', endDate);
    }

    const { data: usageData, error: usageError } = await usageQuery.order('created_at', { ascending: false });

    if (usageError) {
      console.error('Error fetching estimation usage:', usageError);
      return null;
    }

    // Get marketing leads from estimation_form
    let leadsQuery = supabase
      .from('marketing_leads')
      .select('id, created_at, property_address, property_city, raw_data')
      .eq('source', 'estimation_form');

    if (startDate) {
      leadsQuery = leadsQuery.gte('created_at', startDate);
    }
    if (endDate) {
      leadsQuery = leadsQuery.lte('created_at', endDate);
    }

    const { data: leadsData, error: leadsError } = await leadsQuery.order('created_at', { ascending: false });

    if (leadsError) {
      console.error('Error fetching estimation leads:', leadsError);
    }

    // Group by date
    const usageByDate = new Map<string, number>();
    const leadsByDate = new Map<string, number>();
    const citiesCount = new Map<string, number>();

    usageData?.forEach((usage: any) => {
      const date = new Date(usage.created_at).toISOString().split('T')[0];
      usageByDate.set(date, (usageByDate.get(date) || 0) + 1);
    });

    leadsData?.forEach((lead: any) => {
      const date = new Date(lead.created_at).toISOString().split('T')[0];
      leadsByDate.set(date, (leadsByDate.get(date) || 0) + 1);

      // Extract city from address or property_city
      const city = lead.property_city || 
                   (lead.property_address ? lead.property_address.split(',').pop()?.trim() : null) ||
                   (lead.raw_data?.propertyDetails?.address ? lead.raw_data.propertyDetails.address.split(',').pop()?.trim() : null);
      
      if (city) {
        citiesCount.set(city, (citiesCount.get(city) || 0) + 1);
      }
    });

    // Calculate totals
    const totalUsage = usageData?.length || 0;
    const totalLeads = leadsData?.length || 0;
    const totalCost = usageData?.reduce((sum: number, u: any) => sum + (u.cost_usd || 0), 0) || 0;
    const totalTokens = usageData?.reduce((sum: number, u: any) => sum + (u.total_tokens || 0), 0) || 0;

    // Top cities
    const topCities = Array.from(citiesCount.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([city, count]) => ({ city, count }));

    // Usage by date (sorted)
    const usageByDateArray = Array.from(usageByDate.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, count]) => ({ date, count }));

    // Leads by date (sorted)
    const leadsByDateArray = Array.from(leadsByDate.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, count]) => ({ date, count }));

    // Detailed list with lead links
    const detailedList = (usageData || []).map((usage: any) => {
      // Find corresponding lead by matching creation time (within 5 seconds)
      const usageTime = new Date(usage.created_at).getTime();
      const correspondingLead = leadsData?.find((lead: any) => {
        const leadTime = new Date(lead.created_at).getTime();
        return Math.abs(leadTime - usageTime) < 5000; // 5 seconds tolerance
      });

      return {
        id: usage.id,
        createdAt: usage.created_at,
        tokens: usage.total_tokens,
        cost: usage.cost_usd,
        leadId: correspondingLead?.id || null,
        city: correspondingLead?.property_city || 
              (correspondingLead?.property_address ? correspondingLead.property_address.split(',').pop()?.trim() : null) ||
              (correspondingLead?.raw_data?.propertyDetails?.address ? correspondingLead.raw_data.propertyDetails.address.split(',').pop()?.trim() : null),
      };
    });

    return {
      totalUsage,
      totalLeads,
      totalCost,
      totalTokens,
      topCities,
      usageByDate: usageByDateArray,
      leadsByDate: leadsByDateArray,
      detailedList,
    };
  },
};
